/* WRAPPED IN AN IIFE BECAUSE THE STOCK PAGE NOW LOADS EVERY APP AT ONCE (15/08/2026).
   These were four separate pages, so each could declare `escapeHtml`, `spinnerHtml`,
   `MONTHS` and friends at the top level harmlessly. On one page they share ONE global
   scope, and a second `const escapeHtml` is a SyntaxError that kills the WHOLE script -
   so `customElements.define` never ran and every tab after Stock check sat on its
   spinner for ever. Nothing here is used across files; the wrapper simply stops them
   seeing each other. Same shape as the `money` collision in the sync's inlined modules. */
(function () {
/* Manager's stock check overview.
 *
 * Answers one question: what should the shop count next? It lists every section
 * that can be signed off, oldest check first, and lets a manager tick the ones
 * that matter so they surface at the top of the counting page staff use.
 *
 * ORDERING IS THE POINT, so it is deliberate rather than incidental:
 *   1. sections nobody has EVER checked, oldest-created first
 *   2. then everything else, longest-ago first
 * A section never checked is a bigger gap than one checked a year ago, because at
 * least the second one has a known figure behind it.
 *
 * ONLY SECTIONS THAT CAN ACTUALLY BE SIGNED OFF ARE LISTED. A sign-off covers a
 * node's own products and nothing nested inside it (Jonathan, 05/08/2026), so a
 * parent that only holds other sections can never go green and would sit at the
 * top of this list forever, permanently unactionable. Where the server does not
 * tell us (older Stock Ops), everything is shown rather than guessing.
 *
 * HIGH DEMAND IS A BRAKE. Pokemon and similar sell out to bots within minutes of
 * appearing online, so anything flagged here must never have its stock pushed to the
 * website automatically. It applies to a whole till section AND to individual products
 * that sit outside any flagged section, which is why there are two ways to set it.
 *
 * DEGRADES ON PURPOSE. The priority, problems and high-demand operations are newer
 * than this page's first deploy. If the server does not answer them yet, the section
 * list still renders and says plainly which part is unavailable - a manager gets the
 * overview either way instead of an error page.
 *
 * Deploy note (05/08/2026, learned on stock-check-app.js): if this asset starts
 * returning 504 from Shopify's CDN after a push, re-pushing identical bytes will
 * NOT fix it - the URL carries a content hash. Change the contents to mint a new
 * versioned URL.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DAY_MS = 24 * 60 * 60 * 1000;

// Swaps a tick box for a spinning wheel while its save is in flight; a greyed box
// alone looked as if nothing had happened. Returns the undo for a failed save - a
// successful one re-renders the row, which replaces both.
function tickBusy(box) {
  const wheel = document.createElement('span');
  wheel.className = 'stock-manager__spinner stock-manager__tick-spinner';
  wheel.setAttribute('role', 'status');
  wheel.setAttribute('aria-label', 'Saving');
  box.hidden = true;
  box.after(wheel);
  return () => {
    wheel.remove();
    box.hidden = false;
  };
}

async function postOp(url, secret, operation, payload, signal) {
  const response = await (window.dcFetch || fetch)(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(Object.assign({ secret: secret, operation: operation }, payload)),
    signal: signal,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `Request failed (${response.status})`);
  if (data && data.error) throw new Error(data.error);
  return data;
}

/**
 * `syncOne` only, because ITS REFUSALS ARE ANSWERS rather than errors.
 *
 * `postOp` throws whenever the body carries an `error`, which is right for every
 * other operation here - there is nothing to do with a failed tick but say so. But
 * `syncOne` replies with outcomes the page has to act on that carry an `error`
 * alongside them: a high-demand release is asking for a second click, and a failed
 * repoint has to be shouted about rather than filed under "couldn't save that".
 * Turning those into an exception throws away every field except the message.
 */
async function postSyncOp(url, secret, payload) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(Object.assign({ secret: secret, operation: 'syncOne' }, payload)),
  });
  const data = await response.json().catch(() => ({}));
  // The manager gate is the only thing that answers with a non-200, and there is
  // nothing structured to do about it - someone who is not a manager is told so.
  if (!response.ok) throw new Error(data.error || `Request failed (${response.status})`);
  return data;
}

// How long a confirmation stays on screen. Long enough to read a line after settling a
// row, short enough that the page is never carrying a message about something the
// manager has stopped thinking about. Each entry ages on its own clock, so a burst of
// clicks stacks and then empties one line at a time. (Jonathan, 14/08/2026.)
const STATUS_LIFETIME_MS = 10000;

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function ordinalSuffix(day) {
  if (day > 3 && day < 21) return 'th';
  if (day % 10 === 1) return 'st';
  if (day % 10 === 2) return 'nd';
  if (day % 10 === 3) return 'rd';
  return 'th';
}

function formatCheckedAt(value) {
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return '';
  return `${when.getDate()}${ordinalSuffix(when.getDate())} ${MONTHS[when.getMonth()]} ${when.getFullYear()}`;
}

// "43 days ago" reads as urgency in a way a date does not, which is the whole job
// of this page. The date is kept alongside it for anyone reconciling by hand.
function daysAgo(value) {
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return null;
  return Math.floor((Date.now() - when.getTime()) / DAY_MS);
}

function agoLabel(days) {
  if (days == null) return '';
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 31) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? 'about a month ago' : `about ${months} months ago`;
}

/**
 * `13/08/2026 08:52` - the problems table only (Jonathan, 13/08/2026).
 *
 * The sections table keeps `13th Aug 2026`: there, the date is prose in a sentence
 * about when someone last counted. Here it is a timestamp being scanned down a
 * column, and the TIME matters - several of these are raised minutes apart by the
 * same run, and which came first is the thing you want to know.
 */
function formatStamp(value) {
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(when.getDate())}/${pad(when.getMonth() + 1)}/${when.getFullYear()} `
    + `${pad(when.getHours())}:${pad(when.getMinutes())}`;
}

function spinnerHtml(label) {
  return `<p class="stock-manager__loading" role="status">
    <span class="stock-manager__spinner" aria-hidden="true"></span>${escapeHtml(label)}</p>`;
}

/**
 * The products that sit underneath a flagged master, shown beneath it.
 *
 * The till keeps ONE record for a booster box and its loose boosters - sealed
 * boxes in one figure, loose singles in another - while the website sells them
 * as two separate products. So a flagged box is only half the picture, and this
 * is the other half: the loose count on each side (Jonathan, 10/08/2026).
 *
 * **Both figures are LOOSE counts, and the Shopify one is derived rather than
 * read.** The website's booster listing counts every booster it could sell,
 * including those still inside sealed boxes - one box of 24 plus three loose
 * reads 27 there. Showing 27 against the till's 3 invited the reader to think
 * something was broken, so the server subtracts the boxes back out and sends the
 * comparable number. Do not "fix" this by showing the listing's own total.
 *
 * **No Sync button here, deliberately.** His rule: either you sync all of it,
 * master and the products under it, or you sync none of it - so there is exactly
 * one button, on the master. No tick box either: high demand is set on the
 * record that carries the stock, so a tick here would look effective and do
 * nothing.
 */
/**
 * The two figures this page is actually about: what the shop HAS, and what a
 * customer can BUY (Jonathan, 21/08/2026).
 *
 * The page used to read EposNow -> Shopify, where the second number was the website
 * TOTAL - and for a held-back product the total is deliberately the same as the till,
 * so the row showed "194 -> 194" and told the reader nothing about the thing the page
 * exists for. On sale is what matters here; releasing is what the page does.
 *
 * THE TOTAL STILL MATTERS, as a check rather than a column. This page assumes the till
 * and the website hold the same stock, some of it simply not for sale. Where that is
 * not true, something is wrong that no amount of releasing will fix, so the stock
 * figure carries a warning triangle saying by how much - and the number shown is the
 * TILL's, because that is the shop's own count.
 */
function levelsHtml(row) {
  const till = row.eposStock;
  const onSale = row.onSale;
  const held = row.heldStock;
  const known = till != null && onSale != null && held != null;
  // A PRE-ORDER'S PRESOLD UNITS ARE STILL ON THE TILL (13/10/2026): they are only taken off it when
  // the pre-order ends on its sell-from day, so until then they belong in the website's total too.
  const presold = Number(row.presold) || 0;
  const websiteTotal = known ? Number(onSale) + Number(held) + presold : null;
  const gap = known ? Number(till) - websiteTotal : 0;

  const warning = gap === 0 ? '' : ` <abbr class="stock-manager__warn" title="${escapeHtml(
    `The shop counts ${till} but the website holds ${websiteTotal} (${onSale} on sale, ${held} held back`
    + `${presold ? `, ${presold} presold` : ''})`
    + ` - a difference of ${gap > 0 ? gap : -gap}. Releasing cannot fix this; the two need reconciling.`,
  )}">&#9888;</abbr>`;

  // Two short lines, number then words, so it reads as "6 in stock, 0 on sale" (Jonathan,
  // 15/10/2026: the old stacked caps with an arrow between them were clunky).
  return `<div class="stock-manager__levels">
          <b>${escapeHtml(till == null ? '—' : till)}</b><span>in stock${warning}</span>
          <b>${escapeHtml(onSale == null ? '—' : onSale)}</b><span>on sale</span>
        </div>`;
}

/**
 * WHAT KIND OF PROBLEM THIS IS, as a short flag (Jonathan, 13/08/2026).
 *
 * A copy of `scripts/problems/flags.mjs`, which is where the tests live - a theme
 * asset cannot import from the repo, so this is the same hand-kept arrangement as
 * the sync's inlined n8n copies. Change one, change both.
 *
 * The flag comes off the ProblemID rather than being stored, because the id is
 * already stable per product and kind, so nothing needs backfilling and no
 * existing row changes meaning.
 */
const PROBLEM_FIELD_WORD = { taxable: 'tax', productType: 'type', costPrice: 'cost' };

// The name of the value a "value missing" row is about, as its own table shows it.
const MISSING_VALUE_NAME = {
  barcode: 'Barcode', sku: 'SKU', price: 'Price', costPrice: 'Cost price', weight: 'Weight',
  taxable: 'VAT', productType: 'Category', status: 'On sale', listingName: 'Website name',
  compareAtPrice: 'RRP',
};

const PROBLEM_FORMAT_BY_FIELD = {
  stock: 'count', units: 'count', price: 'money', costPrice: 'money',
  weight: 'text', taxable: 'text', status: 'text', productType: 'text',
  barcode: 'text', sku: 'text',
};

// Ordered, first match wins. `null` for the flag means "take the word from the
// field name", which is how a field added to the sync later gets a flag here
// without a new rule.
const PROBLEM_RULES = [
  // NOT "negative stock" - NOTHING IS NEGATIVE. This is raised when a reduction was
  // CLAMPED so that it could not go below zero, which is the opposite state. Jonathan,
  // 14/08/2026: "the negative stock issues are displaying values that are not negative."
  // They were not: the flag described a number that had been PREVENTED from existing,
  // while the two figures beside it are today's, which are ordinarily positive. The row
  // read as data nobody could make sense of.
  [/^sync-stock-would-go-negative-/, 'stopped at zero', 'stock'],
  [/^sync-stock-disagreement-/, 'stock mismatch', 'stock'],
  // The loose-singles gap is just a stock gap (Jonathan, 13/08/2026).
  [/^sync-unit-stock-disagreement-/, 'stock mismatch', 'units'],
  [/^sync-high-demand-restock-held-back-/, 'held back', 'stock'],
  // NOTHING IS NEVER WRITTEN OVER SOMETHING (Jonathan, 31/08/2026). A value that went
  // from a figure to nothing is read as unread rather than deleted, and raises this
  // instead of a write. The field is the id's own capture, so the pair shown is the one
  // that went missing.
  [/^sync-([a-zA-Z]+)-value-missing-/, 'value missing', 'FROM_ID'],

  [/^sync-([a-zA-Z]+)-disagreement-/, null, null],
  // The FIELD is the last segment of the id, so the pair shown is the one that
  // actually failed - these were showing stock figures against a productType failure.
  [/^sync-cannot-apply-\d+-([a-zA-Z]+)$/, 'write failed', 'FROM_ID'],
  [/^sync-cannot-apply-/, 'write failed', 'stock'],
  // REFUSED is not the same as FAILED, and the two must stay separate words (15/08/2026).
  // A 4xx or a `userErrors` refusal is the far side saying no and raises a problem; a 5xx
  // is a hiccup and stays a quiet retry. `write failed` above is the shelved-action case.
  // The field is the id's last segment, so the pair shown is the one that was refused -
  // 177 weight rows and 33 costPrice rows were rendering UNFLAGGED with no figures.
  [/^sync-write-refused-\d+-([a-zA-Z]+)$/, 'write refused', 'FROM_ID'],
  [/^sync-write-refused-/, 'write refused', 'stock'],
  // A listing the sync tried to create and could not. Distinct from `no listing`, which
  // is a product that never had one; here the attempt was made and did not land.
  [/^sync-listing-not-created-/, 'listing failed', null],
  // NO PAIR ON PURPOSE. The row says a listing was drafted for want of artwork; the till
  // and website figures are not what is wrong with it, and `Build Problems` auto-clears
  // any problem whose pair agrees, so giving it stock would clear it the moment the two
  // sides matched - which they usually do.
  [/^sync-no-image,?-created-as-draft-/, 'no image', null],
  [/^sync-partial-write-/, 'half written', 'stock'],
  [/^sync-uncertain-movement-/, 'unconfirmed', 'stock'],
  [/^sync-till-changed-elsewhere-/, 'changed elsewhere', 'stock'],
  [/^sync-run-refused/, 'run refused', null],
  [/^sync-baseline/, 'memory reset', null],
  [/^sync-state-mass-rewrite/, 'memory rewrite', null],
  [/^sync-no-listing-/, 'no listing', 'stock'],
  [/^sync-not-published-/, 'not on sale', null],

  // The run-level alarms. Each is about the RUN rather than one product, so each
  // builds its own id and none carries a pair. Written out here rather than left to
  // `deriveFlag` only because these are the loudest rows the page can show and a
  // four-word chip is read faster than a nine-word one.
  [/^sync-too-many-products-vanished/, 'mass vanish', null],
  [/^sync-too-many-listings/, 'too many new', null],
  [/^sync-held-stock-figure-could-not-be-read-/, 'held unreadable', null],
  [/^sync-product-cache-row-has-no-name-/, 'cache row blank', null],
  [/^sync-an-asmodee-delivery-email/, 'no PDF attached', null],
  // `cache-` is what the stock cache raises from 26/08/2026. The `store-` forms are
  // the rows already on the tab and keep their labels - nothing rewrites an id.
  [/^cache-drift-/, 'cache drift', 'stock'],
  [/^cache-stalled-/, 'cache stalled', null],
  [/^cache-vanished-/, 'stock row gone', 'stock'],
  [/^cache-no-volume-/, 'no pack size', null],
  // `store-`, not `stock-store-`: the id strips the kind's prefix, so these three
  // could never match and 20 rows have been rendering unflagged. Fixed 26/08/2026.
  [/^store-drift-/, 'cache drift', 'stock'],
  [/^store-stalled-/, 'cache stalled', null],
  [/^store-vanished-/, 'stock row gone', 'stock'],
  [/^store-no-volume-/, 'no pack size', null],
  [/^product-vanished-/, 'product gone', null],
  [/^product-bulk-change/, 'bulk change', null],
  [/^delivery-over-/, 'over-delivered', 'stock'],
  // A SHORT DELIVERY CARRIES NO TILL/WEBSITE PAIR ON PURPOSE (16/08/2026). The two
  // numbers that matter are expected against arrived, which live in the summary; the
  // till and the website can agree perfectly while three boxes are still owed. Giving
  // it a field here would be worse than useless - `Build Problems` auto-clears any
  // problem whose pair agrees, so Poetry for Neanderthals (till 1, website 1) would
  // clear itself the moment a manager opened the page.
  [/^delivery-short-/, 'short delivery', null],
  [/^delivery-unknown-/, 'unknown product', null],
  [/^delivery-ambiguous-/, 'ambiguous code', null],
  [/^stock-processing-not-arrived-/, 'not arrived', 'stock'],

  // EVERY SUPPLIER DOCUMENT PROBLEM IS ONE KIND: `delivery note` (Jonathan, 01/10/2026).
  // These ids carry the document number and the supplier's code, so read as a flag they
  // made one chip per delivery note - "bliss 232553 KONLDU9" - which is not a category.
  // The note and the product are in the row's own summary. Ordered before `gw-order-`'s
  // old label, which this replaces. The overdue-sleeve-order alarm is NOT a document.
  [/^piripiri-order-stale-/, 'sleeve order overdue', null],
  [/^(asmodee-delivery|bliss|heo|piripiri|mavengames|gw-order)-/, 'delivery note', null],
  // Approval-time refusals from Deliveries Ops, one per till product.
  [/^delivery-till-/, 'approval refused', null],
  [/^delivery-image-/, 'picture refused', null],
  [/^event-date-/, 'event date', null],
  // ONE CHIP FOR EVERY EVENTS-SHEET LINK FINDING (Jonathan, 08/10/2026): Events Link Check raises
  // several kinds (nobody, tworows, count, badlink, column, vgcmix, unknowntab, notab, twotabs).
  [/^event-link-/, 'event link', null],
];

// The words in an id that say where it came from, not what went wrong.
const SOURCE_WORDS = new Set(['sync', 'cache', 'store', 'product', 'delivery', 'gw']);

// A readable flag for an id no rule matched, read back out of the id itself.
//
// `unflagged` was never a label - it was the page giving up (Jonathan, 31/08/2026:
// "We never want it to say unflagged if possible."). Nine distinct id shapes were
// rendering that way, 350-odd rows between them. It works because `problemId` builds
// `sync-<kind>-<tillId>` with the kind's spaces turned into hyphens, so the kind is
// still in there in plain English and only needs turning back - which is also why a
// fixed list of rules can never be complete, the kind being free text.
//
// The trailing product id goes, so every row of one kind collapses to ONE filter
// group rather than one per product.
function deriveFlag(id) {
  const parts = id.split('-').filter(function (part) { return part !== ''; });
  if (parts.length && SOURCE_WORDS.has(parts[0].toLowerCase())) parts.shift();
  // Everything from the first token carrying a digit goes - a product id, document
  // number, date or supplier code - so a derived flag is a category, never one chip
  // per document. Ids with words after a number have rules above.
  var firstNumbered = parts.findIndex(function (part) { return /\d/.test(part); });
  if (firstNumbered >= 0) parts.length = firstNumbered;
  const words = parts.join(' ').replace(/,/g, '').replace(/\s+/g, ' ').trim();
  if (!words) return null;
  return words.length > 40 ? words.slice(0, 39).replace(/\s+$/, '') + '…' : words;
}

// An id no rule matches is read back out of itself by `deriveFlag`; only an id with
// nothing legible left falls through to `unflagged`. Either way it shows in the list
// and in the filter as its own group: a problem that cannot be labelled must still be
// visible. A derived flag carries NO field, so it shows dashes rather than two figures
// and `syncablePair` can never offer an arrow on it - the page may guess at a name,
// but it must not guess at an action.
function problemFlag(problemId) {
  const id = String(problemId == null ? '' : problemId).trim();
  if (!id) return { flag: 'unflagged', field: null, format: 'none' };
  for (const rule of PROBLEM_RULES) {
    const hit = rule[0].exec(id);
    if (!hit) continue;
    if (rule[1] === null) {
      const raw = hit[1];
      return {
        flag: `${PROBLEM_FIELD_WORD[raw] || raw} mismatch`,
        field: raw,
        format: PROBLEM_FORMAT_BY_FIELD[raw] || 'text',
      };
    }
    // `FROM_ID` keeps the flag but takes the field from the id's own capture.
    const field = rule[2] === 'FROM_ID' ? hit[1] : rule[2];
    return {
      flag: rule[1],
      field: field,
      format: field ? (PROBLEM_FORMAT_BY_FIELD[field] || 'text') : 'none',
    };
  }
  const derived = deriveFlag(id);
  return { flag: derived || 'unflagged', field: null, format: 'none' };
}

/**
 * The product's own name, without the sentence the sync wrapped it in.
 *
 * Every sync problem is written `${name}: what happened`, so the name is what comes
 * before the colon - but it must be the LAST one, not the first (13/08/2026).
 * **3,178 till products have a colon in their own name**, a quarter of the catalogue,
 * so splitting on the first turned `DBS: Unison Warrior (B10) - Booster Box` into
 * `DBS` and `Magic: The Gathering ...` into `Magic`. Jonathan spotted it on the live
 * page: "Several of these products don't seem to have the full product names".
 *
 * Splitting on the last colon is safe because the appended sentence never contains
 * one - "till and website stock disagree", "stock could not be reduced any further",
 * "N added at the till, deliberately NOT put on the website" and the rest are all
 * colon-free. A new summary wording WITH a colon in it would break this, which is a
 * good reason to keep those sentences plain.
 *
 * A problem about no single product - a refused run - has no colon at all, and its
 * summary IS the thing to show, so it comes back whole.
 */
function problemName(problem) {
  const summary = String(problem.summary == null ? '' : problem.summary);
  // A summary that QUOTES its name (`"Star Trek: Fleet Captains" could not be matched to
  // BoardGameGeek`) is read by its quotes: the name's own colon must not cut it (22/10/2026).
  const quoted = /^"([^"]+)"\s/.exec(summary);
  if (quoted) return quoted[1];
  const at = summary.lastIndexOf(': ');
  return at > 0 ? summary.slice(0, at) : summary || 'Problem';
}

/**
 * `Accessories → Dice → Maven Games`, walked up from the product's own section.
 *
 * Found BY NAME, because that is what the server can send for free: a problem row
 * carries the till's `productType`, which IS the till's leaf category name (verified
 * 08/08/2026 - 7,442 of 7,479 matched products agree). Sending an id would have meant
 * a second read of the product cache for every problem.
 *
 * Returns the leaf section too, so the caller can offer its priority tick without
 * looking it up again.
 */
function sectionPathByName(sections, name) {
  const wanted = String(name == null ? '' : name).trim().toLowerCase();
  if (!wanted) return { names: [], leaf: null };
  const byId = new Map(sections.map((s) => [String(s.id), s]));
  const leaf = sections.find((s) => String(s.name).trim().toLowerCase() === wanted);
  if (!leaf) return { names: [], leaf: null };

  const names = [];
  let node = leaf;
  const seen = new Set();
  while (node && !seen.has(String(node.id))) {
    seen.add(String(node.id));
    names.unshift(node.name);
    node = node.parentId ? byId.get(String(node.parentId)) : null;
  }
  return { names: names, leaf: leaf };
}

/**
 * THE PLUMBING IS NOT THE SHOP'S BUSINESS (Jonathan, 13/08/2026).
 *
 * *"They won't understand what a cache is or what it's for."* These are raised and
 * recorded as before; they are reported to Jonathan rather than shown to staff.
 * Anything NOT listed here is shown - so a new kind of problem reaches a human
 * rather than quietly going to a list nobody reads.
 */
const PROBLEM_ENGINEERING_FLAGS = new Set([
  'cache drift', 'cache stalled', 'stock row gone', 'no pack size',
  // 'run refused' is shop-facing since 26/10/2026 - see scripts/problems/flags.mjs.
  'product gone', 'bulk change', 'memory reset',
  // Not a fault at all - the high-demand flag doing its job. Nothing raises it
  // now; this keeps the rows from before 13/08/2026 out of the list.
  'held back',
]);

/**
 * Flags where WHY it happened is the useful part, so the reason earns its line.
 *
 * `write failed` is the one Jonathan asked for: "a change to stock could not be
 * carried out" says nothing on its own, where "no Shopify inventory item known
 * for this product" tells you whether it is yours to fix.
 */
const PROBLEM_REASON_FLAGS = new Set([
  'write failed', 'half written', 'unconfirmed', 'changed elsewhere',
]);

/**
 * The reason, which is the FIRST sentence of the detail and never the whole thing.
 *
 * The rest of every detail is the standing explanation of what the sync does about
 * it - true, and the paragraph this row exists to get rid of.
 */
function problemReason(problem) {
  const detail = String(problem.detail == null ? '' : problem.detail).trim();
  if (!detail) return '';
  const stop = detail.indexOf('. ');
  return stop > 0 ? detail.slice(0, stop + 1) : detail;
}

/**
 * The fields a manager may settle from this page with an arrow, and no others.
 *
 * An ALLOW-list, for the same reason the page's role gate is one: a field the sync
 * learns about later gets no arrows here until somebody adds it deliberately and has
 * checked there is a write path for it, so a control that changes a live shop fails
 * closed rather than open.
 *
 * `units` is deliberately absent. It is the loose-singles count carried by a MASTER
 * product, which is a different write from an ordinary stock figure - a booster box
 * and its loose boosters are one master with two counts - and putting the same arrow
 * on both would quietly send one of them down the wrong path.
 *
 * `productType` and `taxable` are absent because they are till-master: the sync
 * ignores a website change to either, so an arrow pointing at the till would be
 * offering something the system is built to refuse.
 */
const SYNCABLE_FIELDS = new Set(['stock', 'price', 'costPrice']);

/**
 * Whether this row is a two-sided disagreement one click can settle.
 *
 * All three tests rule out a real row on the live page, so none is theoretical:
 *
 * - **The flag must BE a mismatch.** `negative stock` and `held back` both carry a
 *   stock pair, and neither is two sides disagreeing about the same fact - one is a
 *   clamp doing its job and the other is the high-demand rule doing its job.
 * - **The field must be one we can write** (above).
 * - **Both values must be present.** `no listing` has a till figure and no website
 *   one, and copying an absent value is not a fix - it is a different bug.
 */
function syncablePair(flag, field, pair) {
  if (!field || !SYNCABLE_FIELDS.has(field)) return false;
  if (!/ mismatch$/.test(String(flag))) return false;
  const absent = (v) => v === null || v === undefined || v === '';
  return !absent(pair.till) && !absent(pair.web);
}

/**
 * KINDS THAT EARN A TABLE OF THEIR OWN (Jonathan, 16/09/2026).
 *
 * *"Is it possible for the rows in the table to adapt to the kind of problem it is?
 * Maybe have different tables on this page for different sorts of problems."*
 *
 * The generic table's columns - Till, Sync, Website - describe a DISAGREEMENT between
 * two sides, which is what most problems are. A short delivery is not: there is no
 * website figure to argue with, and the three numbers that matter (ordered, arrived,
 * short by) had nowhere to go, so they were written into a sentence nobody can scan.
 *
 * THE FIGURES COME OFF `problem.data`, NEVER OUT OF THE PROSE. The raiser puts them
 * there; a page that parsed the detail would break the first time somebody reworded
 * it, and rewording explanatory text is exactly the sort of change nobody expects to
 * break anything.
 *
 * A PROBLEM WITH NO `data` FALLS BACK TO THE GENERIC TABLE rather than being dropped
 * or shown with blank cells. A row that vanishes because a field is missing is worse
 * than a row that looks plain - the whole point of this page is that nothing outstanding
 * is invisible.
 */
const PROBLEM_TABLES = {
  'short delivery': {
    caption: 'Short deliveries',
    lead: 'What the supplier still owes. The stock that did arrive is already booked in.',
    columns: [
      { label: 'Product', className: 'stock-manager__name',
        cell: (d) => escapeHtml(String(d.product == null ? '' : d.product)) },
      { label: 'Ordered', className: 'stock-manager__num',
        cell: (d) => escapeHtml(problemValue(d.expected, 'count')) },
      { label: 'Arrived', className: 'stock-manager__num',
        cell: (d) => escapeHtml(problemValue(d.arrived, 'count')) },
      { label: 'Short by', className: 'stock-manager__num',
        cell: (d) => escapeHtml(problemValue(d.short, 'count')) },
      { label: 'Delivery', className: 'stock-manager__path',
        cell: (d) => (d.delivery
          ? escapeHtml(String(d.delivery))
          : '<span class="stock-manager__dash">&mdash;</span>') },
    ],
  },
};

/** A new-product problem the nightly check has broken into fillable lines. */
function isNewProductWithIssues(problem) {
  return Boolean(problem && /^new-product-\d+$/.test(String(problem.id))
    && problem.data && Array.isArray(problem.data.issues) && problem.data.issues.length);
}

/** `New Product Fix` lives beside Stock Ops Admin and takes the same secret. */
function newProductFixUrl(adminUrl) {
  return `${new URL(adminUrl).origin}/webhook/new-product-fix`;
}

/** A pair of values written the way its flag implies. */
function problemValue(value, format) {
  if (value === null || value === undefined || value === '') return '—';
  if (format === 'money') return `£${Number(value).toFixed(2)}`;
  if (format === 'count') return String(value);
  if (format === 'text') return String(value);
  return '—';
}

class StockCheckManager extends HTMLElement {
  constructor() {
    super();
    // Two endpoints, deliberately. The section list comes from Stock Ops, which the
    // whole shop already reads; the three manager writes go to Stock Ops Admin, which
    // has its own secret so that opening the counting page never hands anyone the
    // ability to change what others are asked to count.
    this.url = this.dataset.url;
    this.secret = this.dataset.secret;
    this.adminUrl = this.dataset.adminUrl;
    this.adminSecret = this.dataset.adminSecret;
    this.customerId = this.dataset.customerId;
    this.sections = [];
    this.problems = [];
    this.problemsAvailable = true;
    this.priorityAvailable = true;
    this.highDemandAvailable = true;
    this.hdCategories = new Set();
    this.hdProducts = [];
    this.searchResults = null;
    this.searchTimer = null;
    // The search shows what the shop actually has, and hides the rest behind a
    // tick (Jonathan, 10/08/2026). Searching "collector" matched 106 products
    // and the page could show 25 of them, so the one he wanted - MTG The Hobbit
    // Collector Box - was simply absent, with nothing saying so. In stock only,
    // that same search is 19.
    this.showOutOfStock = false;
    this.searchTotal = 0;
    this.searchTruncated = false;
    // TABS, NOT COLLAPSIBLE PANELS (Jonathan, 13/08/2026). Which one is showing is
    // held on the element rather than in the markup, for the same reason the panels'
    // open state was: the page re-renders on every tick, and a tab that jumped back to
    // the first one under a manager mid-edit would be worse than no tabs at all.
    this.activeTab = 'problems';
    // MORE THAN ONE KIND AT A TIME (Jonathan, 13/08/2026), so this is a Set and an
    // EMPTY one means every kind. Kept on the element rather than in the markup,
    // like the panels, because the page re-renders on every tick and a filter that
    // reset itself under a manager mid-read would be worse than not having one.
    this.problemFilters = new Set();
    // WHICH SYNC ARROW IS ARMED, as `${problemId}|${direction}`, or null for none.
    // Only ever one: arming a second disarms the first, so there is never a page
    // holding two live writes waiting for a second click.
    this.armedSync = null;
    // The SECOND confirmation, and only ever for a high-demand release. The server
    // refuses to put stock onto a high-demand listing without an explicit say-so,
    // because that flag exists precisely to make a person decide - so the page holds
    // which arrow has been asked about and is waiting to be released.
    this.highDemandArmed = null;
    // A release puts stock in front of bots and cannot be taken back once bought,
    // so it arms on the first click and acts on the second - the same shape as
    // the other irreversible controls here.
    this.releaseArmed = null;
    // The number typed beside each Release button, by product id. The tab is redrawn on the first
    // press (to arm it), so a value held only in the box was wiped before the second press could
    // read it (13/10/2026).
    this.releaseQty = new Map();
    // THE HIGH DEMAND TAB BY SECTION (Jonathan, 13/10/2026): the section being looked at, as the
    // server answered for it - every section and product, `{tree, all}` - or null before the first
    // load. `hdLive` holds the website's figures per section once they have been asked for.
    this.hdTree = null;
    this.hdNode = '';
    this.hdLoading = false;
    this.hdLive = new Map();
    this.hdLiveLoading = new Set();
    this.saving = new Set();
    // CLEARED PROBLEMS STAY ON THE PAGE, GREYED, UNTIL THE NEXT LOAD (Jonathan, 28/10/2026).
    this.clearedProblems = new Set();
    // A new product's detail saved this visit (`<problem id>|<index>`), greyed until the next load.
    this.savedIssues = new Set();
    this.controller = null;
    // What has happened in the last few seconds, newest last. Held here rather than in
    // the DOM because `render()` rebuilds the status node along with everything else.
    this.statusEntries = [];
    this.statusTimer = null;
    // The one arrow currently writing, as `problemId|direction`. Its button shows a
    // spinner in place of the glyph; the other arrow on the row is merely disabled.
    this.syncingArrow = null;
  }

  connectedCallback() {
    this.addEventListener('change', this.onChange.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('input', this.onInput.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    // The same picture cell as the Stock check tab: a tap opens every picture, a desk gets a
    // hover preview. Delegated, because the High demand rows are rebuilt on every render.
    if (window.StaffPictures) window.StaffPictures.bind(this);
    // THE PROBLEMS ARE ASKED FOR THE MOMENT THE PAGE LOADS, WHICHEVER TAB IS OPEN (Jonathan,
    // 15/10/2026). Reading the list also clears the problems that have settled, so starting it
    // at once means that by the time anyone reaches Problems that is already done; and if the
    // page opens ON Problems, the list waits for it as before. Everything else in this app is
    // still fetched in its turn.
    this.earlyProblems = postOp(this.adminUrl, this.adminSecret, 'listProblems',
      { customerId: this.customerId });
    this.earlyProblems.catch(() => {});
    (window.dcWhenShown || ((el, fn) => fn()))(this, () => this.load());
  }

  // Left/right along the tab strip, which is what a keyboard user expects of a tablist
  // and what the roving `tabindex` in `tabsHtml` is there to support.
  onKeydown(event) {
    const npInput = event.key === 'Enter' && event.target.closest('[data-np-input]');
    if (npInput) {
      event.preventDefault();
      this.saveNewProductDetail(String(npInput.dataset.npInput));
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const tab = event.target.closest('[data-tab]');
    if (!tab) return;
    const tabs = [...this.querySelectorAll('[data-tab]')];
    const at = tabs.indexOf(tab);
    const next = tabs[(at + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    if (!next) return;
    event.preventDefault();
    this.activeTab = next.dataset.tab;
    this.render();
    const moved = this.querySelector(`[data-tab="${this.activeTab}"]`);
    if (moved) moved.focus();
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    if (this.statusTimer) {
      clearInterval(this.statusTimer);
      this.statusTimer = null;
    }
  }

  /**
   * A LIST OF WHAT JUST HAPPENED, not a single line that the next event overwrites.
   *
   * Jonathan settled two rows in quick succession on 14/08/2026 and only one was
   * confirmed. Two reasons, and the second is the real one:
   *   * each message replaced the last, so the first was gone before it was read; and
   *   * `render()` rebuilds `innerHTML` INCLUDING the status node, so anything already
   *     on screen was destroyed by the next click's re-render regardless.
   * So the messages have to live in state and be painted BY `render()`, not written
   * into the DOM and hoped for.
   *
   * Each entry lasts ten seconds of its own, so a burst stacks up and then empties
   * itself one line at a time, and a single change leaves nothing behind either.
   */
  setStatus(message, isError) {
    // Arming clears nothing now. It used to call this with an empty string to hide the
    // line; under a list that would throw away confirmations the manager has not read.
    if (!message) return;
    if (!this.statusEntries) this.statusEntries = [];
    this.statusEntries.push({ text: String(message), isError: Boolean(isError), at: Date.now() });
    // A cap, because a fast hand on a long list should not build a wall of text.
    if (this.statusEntries.length > 6) this.statusEntries = this.statusEntries.slice(-6);
    this.paintStatus();
    this.startStatusSweep();
  }

  /** Drop anything older than its ten seconds. Returns true if the list changed. */
  pruneStatus() {
    if (!this.statusEntries || !this.statusEntries.length) return false;
    const cutoff = Date.now() - STATUS_LIFETIME_MS;
    const kept = this.statusEntries.filter((entry) => entry.at > cutoff);
    if (kept.length === this.statusEntries.length) return false;
    this.statusEntries = kept;
    return true;
  }

  paintStatus() {
    const node = this.querySelector('[data-status]');
    if (!node) return;
    const entries = this.statusEntries || [];
    node.hidden = entries.length === 0;
    node.classList.toggle('is-error', entries.some((entry) => entry.isError));
    node.innerHTML = entries
      .map((entry) => `<span class="stock-manager__status-line${entry.isError ? ' is-error' : ''}">`
        + `${escapeHtml(entry.text)}</span>`)
      .join('');
  }

  /**
   * One timer for the whole list, stopped as soon as it has nothing left to do - a
   * timer per message would leave strays behind on a page that lives for hours.
   */
  startStatusSweep() {
    if (this.statusTimer) return;
    this.statusTimer = setInterval(() => {
      const changed = this.pruneStatus();
      if (changed) this.paintStatus();
      if (!this.statusEntries || !this.statusEntries.length) {
        clearInterval(this.statusTimer);
        this.statusTimer = null;
      }
    }, 500);
  }

  async load() {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.innerHTML = spinnerHtml('Loading…');

    // ALL THREE ARE ASKED AT ONCE (15/10/2026): none depends on another, and asking them one
    // after another made this tab wait about eight seconds before it drew anything.
    const ask = (url, secret, operation) => postOp(url, secret, operation, { customerId: this.customerId }, signal);
    const [sections, problems, demand] = await Promise.allSettled([
      ask(this.url, this.secret, 'listCategories'),
      // The page-load request is used once; any later reload asks again.
      this.earlyProblems || ask(this.adminUrl, this.adminSecret, 'listProblems'),
      ask(this.adminUrl, this.adminSecret, 'listHighDemand'),
    ]);
    this.earlyProblems = null;
    if (signal.aborted) return;

    if (sections.status === 'rejected') {
      // A FAILURE HERE MUST BE VISIBLE, and for a day it was not (17/08/2026). This
      // used to clear the whole element and then call `setStatus`, which paints into
      // `[data-status]` - a node that had just been destroyed. So the message went
      // nowhere and all three of this app's tabs sat completely blank, saying nothing,
      // while Stock Ops had been crashing since the night before. "Empty" reads as a
      // page half-built; a sentence reads as a fault someone can report.
      this.innerHTML = `<p class="stock-manager__error" data-load-error>`
        + `${escapeHtml(`Couldn't load the sections: ${sections.reason.message}`)}</p>`;
      return;
    }
    this.sections = (sections.value.categories || []).slice();
    // A server that does not send the flag yet must not make every section look
    // un-prioritised AND un-settable; it means "we do not know", not "no".
    this.priorityAvailable = this.sections.some((c) => 'priority' in c);

    // Problems and high demand are separate calls so a failure in either cannot cost the
    // manager the section list, which is the part they came for.
    if (problems.status === 'fulfilled') {
      this.problems = (problems.value.problems || []).filter((p) => String(p.status || 'open') === 'open');
    } else {
      this.problemsAvailable = false;
    }
    if (demand.status === 'fulfilled') {
      this.hdCategories = new Set((demand.value.categories || []).map((c) => String(c.id)));
      this.hdProducts = (demand.value.products || []).slice();
    } else {
      this.highDemandAvailable = false;
    }

    this.render();
  }

  // EVERY section is listed, including ones holding no products (Jonathan, 08/08/2026).
  // An empty shelf is still a shelf, and "nobody has ever checked it" is exactly what
  // this page exists to surface. They sort to the bottom rather than being hidden,
  // because they are not what anyone should count next.
  hasProducts(node) {
    return Number(node.inStockHere) > 0;
  }

  ordered() {
    if (this.sortKey) return this.sortedBy(this.sortKey, this.sortDir);
    return this.sections.slice().sort((a, b) => {
      // Sections with nothing in them go last whatever their history says - there is
      // nothing to count, so they can never be the answer to "what next".
      const aHas = this.hasProducts(a);
      const bHas = this.hasProducts(b);
      if (aHas !== bHas) return aHas ? -1 : 1;
      if (!aHas) return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

      const at = a.lastCheckedAt ? new Date(a.lastCheckedAt).getTime() : null;
      const bt = b.lastCheckedAt ? new Date(b.lastCheckedAt).getTime() : null;
      if (at == null && bt == null) return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      if (at == null) return -1; // never checked sorts above any real date
      if (bt == null) return 1;
      return at - bt; // oldest first
    });
  }

  /**
   * A heading the manager clicked. Until one is clicked the table keeps its own order
   * (what to count next), which is the reason the page exists. Ties fall back to the name
   * so the order never shuffles between renders.
   */
  sortedBy(key, dir) {
    const byName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    const value = (node) => {
      switch (key) {
        case 'name': return 0;
        // Never checked is the oldest of all.
        case 'checked': return node.lastCheckedAt ? new Date(node.lastCheckedAt).getTime() : -Infinity;
        case 'products': return Number(node.inStockHere) || 0;
        case 'priority': return node.priority ? 1 : 0;
        case 'demand': return this.hdCategories.has(String(node.id)) ? 1 : 0;
        default: return 0;
      }
    };
    const sign = dir === 'desc' ? -1 : 1;
    return this.sections.slice().sort((a, b) => {
      if (key === 'name') return sign * byName(a, b);
      // A section with nothing in it cannot be checked, so it goes to the bottom
      // whichever way the dates run (Jonathan, 20/10/2026).
      if (key === 'checked') {
        const aHas = this.hasProducts(a);
        const bHas = this.hasProducts(b);
        if (aHas !== bHas) return aHas ? -1 : 1;
        if (!aHas) return byName(a, b);
      }
      const va = value(a);
      const vb = value(b);
      if (va !== vb) return sign * (va < vb ? -1 : 1);
      return byName(a, b);
    });
  }

  // A section name opens that section on the Stock check tab, so a manager can go
  // straight from "never checked" to counting it. Only offered where that tab exists.
  sectionLink(node) {
    if (!document.querySelector('stock-check')) return escapeHtml(node.name);
    return `<button type="button" class="stock-manager__section-link"
      data-open-section="${escapeHtml(node.id)}">${escapeHtml(node.name)}</button>`;
  }

  sortHeading(key, label, className) {
    const on = this.sortKey === key;
    const arrow = on ? (this.sortDir === 'desc' ? ' &#9660;' : ' &#9650;') : '';
    const aria = on ? (this.sortDir === 'desc' ? 'descending' : 'ascending') : 'none';
    return `<th scope="col"${className ? ` class="${className}"` : ''} aria-sort="${aria}">
        <button type="button" class="stock-manager__sort${on ? ' is-on' : ''}" data-sort="${key}">${label}${arrow}</button>
      </th>`;
  }

  // One row per section, no disclosure. Everything a manager needs to decide what to
  // count next fits on a line, and the point of this page is comparing sections
  // against each other - which you cannot do if they are folded away.
  rowHtml(node) {
    // A dash, not "Never" - an empty section has not been missed, there is simply
    // nothing there to have checked.
    if (!this.hasProducts(node)) {
      return `
      <tr class="stock-manager__row is-empty${node.priority ? ' is-priority' : ''}">
        <td class="stock-manager__name">${this.sectionLink(node)}</td>
        <td class="stock-manager__when"><span class="stock-manager__dash">&mdash;</span></td>
        <td class="stock-manager__num">0</td>
        <td class="stock-manager__tick">
          <label>
            <input type="checkbox" data-priority-for="${escapeHtml(node.id)}"
                   ${node.priority ? 'checked' : ''} ${this.priorityAvailable ? '' : 'disabled'}>
            <span class="visually-hidden">Priority: ${escapeHtml(node.name)}</span>
          </label>
        </td>
        <td class="stock-manager__tick">
          <label>
            <input type="checkbox" data-demand-cat="${escapeHtml(node.id)}"
                   ${this.hdCategories.has(String(node.id)) ? 'checked' : ''}
                   ${this.highDemandAvailable ? '' : 'disabled'}>
            <span class="visually-hidden">High demand: ${escapeHtml(node.name)}</span>
          </label>
        </td>
      </tr>`;
    }

    const never = !node.lastCheckedAt;
    const when = never
      ? '<span class="stock-manager__never">Never</span>'
      : `<span class="stock-manager__ago">${escapeHtml(agoLabel(daysAgo(node.lastCheckedAt)))}</span>
         <span class="stock-manager__date">${escapeHtml(formatCheckedAt(node.lastCheckedAt))}${
           node.lastCheckedBy ? ` &middot; ${escapeHtml(node.lastCheckedBy)}` : ''
         }</span>`;

    return `
      <tr class="stock-manager__row${never ? ' is-never' : ''}${node.priority ? ' is-priority' : ''}">
        <td class="stock-manager__name">${this.sectionLink(node)}</td>
        <td class="stock-manager__when">${when}</td>
        <td class="stock-manager__num">${escapeHtml(node.inStockHere == null ? '—' : node.inStockHere)}</td>
        <td class="stock-manager__tick">
          <label>
            <input
              type="checkbox"
              data-priority-for="${escapeHtml(node.id)}"
              ${node.priority ? 'checked' : ''}
              ${this.priorityAvailable ? '' : 'disabled'}
            >
            <span class="visually-hidden">Priority: ${escapeHtml(node.name)}</span>
          </label>
        </td>
        <td class="stock-manager__tick">
          <label>
            <input
              type="checkbox"
              data-demand-cat="${escapeHtml(node.id)}"
              ${this.hdCategories.has(String(node.id)) ? 'checked' : ''}
              ${this.highDemandAvailable ? '' : 'disabled'}
            >
            <span class="visually-hidden">High demand: ${escapeHtml(node.name)}</span>
          </label>
        </td>
      </tr>`;
  }

  /**
   * Which flags are actually present, commonest first, for the filter.
   *
   * **Only flags in the list are offered** (Jonathan, 13/08/2026). There are ~30
   * possible and 8 in use; a filter with 22 empty options is worse than none.
   */
  /** The problems staff should see - the plumbing is filtered out, not fixed here. */
  shopProblems() {
    // A problem log SENT to Asmodee is not a problem (Jonathan, 16/10/2026): Jotform's
    // confirmation in contact@ says so. Its row stays open only as the record the credit note is
    // matched against. One that failed to send (`asmodee-log-failed-`) still shows.
    return this.problems.filter((p) => !PROBLEM_ENGINEERING_FLAGS.has(problemFlag(p.id).flag)
      && !/^asmodee-log-(?!failed-)/.test(String(p.id || '')));
  }

  problemFlagCounts() {
    const counts = new Map();
    for (const p of this.shopProblems()) {
      const { flag } = problemFlag(p.id);
      // A kind whose rows are all cleared keeps its chip at 0 until the page reloads, so a filter
      // on it still has a chip to un-pick and its greyed rows do not jump to another view.
      const cleared = this.clearedProblems.has(String(p.id));
      counts.set(flag, (counts.get(flag) || 0) + (cleared ? 0 : 1));
    }
    return [...counts].sort((a, b) => {
      if (a[0] === 'unflagged') return 1;
      if (b[0] === 'unflagged') return -1;
      if (b[1] !== a[1]) return b[1] - a[1];
      return a[0].localeCompare(b[0]);
    });
  }

  /**
   * One line per problem - no prose (Jonathan, 13/08/2026). The old list printed a
   * paragraph each, which cannot be scanned or filtered; the flag plus the two
   * figures says the same thing.
   *
   * **The two value columns are headed `Till` and `Website` and nothing more
   * specific**, because what they hold depends on the flag: a price mismatch shows
   * the two prices where a stock one shows the counts. The flag chip is what tells
   * you which, so a header saying "Till stock" would be wrong on half the rows.
   */
  problemsBody() {
    if (!this.problemsAvailable) {
      return '<p class="stock-manager__muted">The problems list isn&rsquo;t switched on yet.</p>';
    }
    const all = this.shopProblems();
    if (!all.length) {
      return '<p class="stock-manager__muted">Nothing outstanding.</p>';
    }

    const counts = this.problemFlagCounts();
    // Any of the chosen kinds, not all of them at once - picking two flags means
    // "show me these two", which is the only reading that makes sense when a
    // problem carries exactly one flag.
    const chosen = this.problemFilters;
    const shown = chosen.size
      ? all.filter((p) => chosen.has(problemFlag(p.id).flag))
      : all;

    const filters = `
        <div class="stock-manager__flag-filter" role="group" aria-label="Filter problems by kind">
          <button type="button" class="stock-manager__chip${chosen.size ? '' : ' is-on'}"
                  data-flag-filter="" aria-pressed="${chosen.size ? 'false' : 'true'}">
            All <small>${all.filter((p) => !this.clearedProblems.has(String(p.id))).length}</small>
          </button>
          ${counts
            .map(
              ([flag, count]) => `<button type="button"
                    class="stock-manager__chip${chosen.has(flag) ? ' is-on' : ''}"
                    data-flag-filter="${escapeHtml(flag)}"
                    aria-pressed="${chosen.has(flag) ? 'true' : 'false'}">
                ${escapeHtml(flag)} <small>${count}</small>
              </button>`,
            )
            .join('')}
        </div>
        ${chosen.size > 1
          ? `<p class="stock-manager__muted stock-manager__filter-note">Showing ${shown.length} of ${all.length}, across ${chosen.size} kinds.</p>`
          : ''}`;

    // A KIND WITH ITS OWN TABLE IS LIFTED OUT; everything else stays in the general one.
    // Lifted only when the row actually CARRIES its figures, so a short delivery raised
    // before the data column existed still appears, in the table that can show it.
    const special = [];
    const generic = [];
    const newProducts = [];
    const valueMissing = [];
    for (const p of shown) {
      if (isNewProductWithIssues(p)) {
        newProducts.push(p);
        continue;
      }
      // A MISSING VALUE IS NOT A STOCK DISAGREEMENT (Jonathan, 26/10/2026): in the general
      // table a barcode sat in the column everyone reads as the website's stock. Its own
      // table names the value and shows what each side holds, with no sync arrows.
      if (problemFlag(p.id).flag === 'value missing') {
        valueMissing.push(p);
        continue;
      }
      const spec = PROBLEM_TABLES[problemFlag(p.id).flag];
      (spec && p.data ? special : generic).push(p);
    }
    const byFlag = new Map();
    for (const p of special) {
      const flag = problemFlag(p.id).flag;
      if (!byFlag.has(flag)) byFlag.set(flag, []);
      byFlag.get(flag).push(p);
    }
    const specialTables = [...byFlag.entries()]
      .map(([flag, rows]) => this.problemKindTableHtml(flag, PROBLEM_TABLES[flag], rows))
      .join('') + (newProducts.length ? this.newProductTableHtml(newProducts) : '')
      + (valueMissing.length ? this.valueMissingTableHtml(valueMissing) : '');

    if (!generic.length) {
      return `${filters}${specialTables}`;
    }

    return `
        ${filters}
        ${specialTables}
        <table class="stock-manager__table stock-manager__problems">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Product</th>
              <th scope="col">Section</th>
              <th scope="col" class="stock-manager__tick">Priority</th>
              <th scope="col" class="stock-manager__num">Till</th>
              <th scope="col" class="stock-manager__sync-col">Sync</th>
              <th scope="col" class="stock-manager__num">Website</th>
              <th scope="col"><span class="visually-hidden">Clear</span></th>
            </tr>
          </thead>
          <tbody>
            ${generic.map((p) => this.problemRowHtml(p)).join('')}
          </tbody>
        </table>`;
  }

  /**
   * One kind, in the columns that kind is actually about.
   *
   * `When` and `Clear` are on every table because they belong to the PROBLEM rather
   * than to its kind: when it was raised, and the button that settles it. Everything
   * between them comes from the kind's own column list.
   */
  problemKindTableHtml(flag, spec, rows) {
    return `
        <table class="stock-manager__table stock-manager__problems stock-manager__problems--${escapeHtml(flag.replace(/\s+/g, '-'))}">
          <caption class="stock-manager__caption">
            ${escapeHtml(spec.caption)} <small>${rows.length}</small>
            ${spec.lead ? `<span class="stock-manager__muted">${escapeHtml(spec.lead)}</span>` : ''}
          </caption>
          <thead>
            <tr>
              <th scope="col">When</th>
              ${spec.columns.map((c) => `<th scope="col" class="${escapeHtml(c.className || '')}">${escapeHtml(c.label)}</th>`).join('')}
              <th scope="col"><span class="visually-hidden">Clear</span></th>
            </tr>
          </thead>
          <tbody>
            ${rows.map((p) => `
              <tr class="stock-manager__row${this.clearedRowClass(p.id)}">
                <td class="stock-manager__when">
                  ${p.raisedAt ? escapeHtml(formatStamp(p.raisedAt)) : '<span class="stock-manager__dash">&mdash;</span>'}
                </td>
                ${spec.columns.map((c) => `<td class="${escapeHtml(c.className || '')}">${c.cell(p.data || {}, p)}</td>`).join('')}
                <td class="stock-manager__act">
                  ${this.resolveButtonHtml(p.id)}
                </td>
              </tr>`).join('')}
          </tbody>
        </table>`;
  }

  /**
   * NEW PRODUCTS MISSING A DETAIL GET THEIR OWN TABLE (Jonathan, 22/10/2026): one row per thing
   * missing, saying what it is, with a box to fill it in like the Deliveries tab, opening on the
   * nightly check's suggestion (a missing RRP suggests the selling price). No Priority, Till,
   * Sync or Website columns - those describe a disagreement, and this is a gap.
   *
   * A line the box cannot fix (VAT, category, tags) shows a dash instead of a box.
   */
  newProductTableHtml(rows) {
    const body = rows.map((p) => {
      const issues = p.data.issues;
      const path = sectionPathByName(this.sections, p.section).names;
      const span = issues.length > 1 ? ` rowspan="${issues.length}"` : '';
      return issues.map((issue, i) => {
        const key = `${p.id}|${i}`;
        const busy = this.saving.has(`np:${key}`);
        const saved = this.savedIssues.has(key) || this.clearedProblems.has(String(p.id));
        const money = issue.field === 'rrp' || issue.field === 'price' || issue.field === 'cost';
        const fill = issue.field
          ? `<span class="stock-manager__np-fill">
               ${money ? '<span aria-hidden="true">£</span>' : ''}
               <input type="text" class="stock-manager__np-input${money ? ' stock-manager__np-input--money' : ''}"
                      data-np-input="${escapeHtml(key)}" value="${escapeHtml(issue.suggest || '')}"
                      ${money ? 'inputmode="decimal"' : ''} ${busy || saved ? 'disabled' : ''}
                      aria-label="${escapeHtml(issue.text)}">
               ${saved
                 ? '<button type="button" class="stock-manager__save" disabled>Saved</button>'
                 : `<button type="button" class="stock-manager__save" ${busy ? 'disabled' : `data-np-save="${escapeHtml(key)}"`}>
                      ${busy ? '<span class="stock-manager__spinner" aria-hidden="true"></span>Saving' : 'Save'}
                    </button>`}
             </span>`
          : '<span class="stock-manager__dash">&mdash;</span>';
        const lead = i === 0
          ? `<td class="stock-manager__when"${span}>
               ${p.raisedAt ? escapeHtml(formatStamp(p.raisedAt)) : '<span class="stock-manager__dash">&mdash;</span>'}
             </td>
             <td class="stock-manager__name"${span}>${escapeHtml(p.data.name || problemName(p))}</td>
             <td class="stock-manager__path"${span}>
               ${path.length
                 ? path.map((n) => escapeHtml(n)).join(' <span aria-hidden="true">&rarr;</span> ')
                 : '<span class="stock-manager__dash">&mdash;</span>'}
             </td>`
          : '';
        const clear = i === 0
          ? `<td class="stock-manager__act"${span}>
               ${this.resolveButtonHtml(p.id)}
             </td>`
          : '';
        return `
          <tr class="stock-manager__row${i ? ' stock-manager__row--cont' : ''}${this.clearedRowClass(p.id)}">
            ${lead}
            <td${saved ? ' class="is-cleared"' : ''}>${escapeHtml(issue.text)}</td>
            <td${saved ? ' class="is-cleared"' : ''}>${fill}</td>
            ${clear}
          </tr>`;
      }).join('');
    }).join('');
    return `
        <table class="stock-manager__table stock-manager__problems stock-manager__problems--new-product">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Product</th>
              <th scope="col">Section</th>
              <th scope="col">Missing</th>
              <th scope="col">Fill in</th>
              <th scope="col"><span class="visually-hidden">Clear</span></th>
            </tr>
          </thead>
          <tbody>${body}</tbody>
        </table>`;
  }

  /** "Value missing" rows: which value, and what the till and the website each hold. */
  valueMissingTableHtml(rows) {
    const body = rows.map((p) => {
      const { field, format } = problemFlag(p.id);
      const pair = (p.values || {})[field] || {};
      const path = sectionPathByName(this.sections, p.section).names;
      return `
          <tr class="stock-manager__row${this.clearedRowClass(p.id)}">
            <td class="stock-manager__when">
              ${p.raisedAt ? escapeHtml(formatStamp(p.raisedAt)) : '<span class="stock-manager__dash">&mdash;</span>'}
            </td>
            <td class="stock-manager__name">
              ${escapeHtml(problemName(p))}
              <span class="stock-manager__flag">value missing</span>
            </td>
            <td class="stock-manager__path">
              ${path.length
                ? path.map((n) => escapeHtml(n)).join(' <span aria-hidden="true">&rarr;</span> ')
                : '<span class="stock-manager__dash">&mdash;</span>'}
            </td>
            <td>${escapeHtml(MISSING_VALUE_NAME[field] || field || '—')}</td>
            <td>${escapeHtml(problemValue(pair.till, format))}</td>
            <td>${escapeHtml(problemValue(pair.web, format))}</td>
            <td class="stock-manager__act">
              ${this.resolveButtonHtml(p.id)}
            </td>
          </tr>`;
    }).join('');
    return `
        <table class="stock-manager__table stock-manager__problems stock-manager__problems--value-missing">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Product</th>
              <th scope="col">Section</th>
              <th scope="col">Missing value</th>
              <th scope="col">On the till</th>
              <th scope="col">On the website</th>
              <th scope="col"><span class="visually-hidden">Clear</span></th>
            </tr>
          </thead>
          <tbody>${body}</tbody>
        </table>`;
  }

  /** Save one filled-in detail onto the till, then drop that line (and the row when it is the last). */
  async saveNewProductDetail(key) {
    const bar = key.lastIndexOf('|');
    const id = key.slice(0, bar);
    const index = Number(key.slice(bar + 1));
    const problem = this.problems.find((p) => String(p.id) === id);
    if (!isNewProductWithIssues(problem)) return;
    const issue = problem.data.issues[index];
    const input = this.querySelector(`[data-np-input="${CSS.escape(key)}"]`);
    const value = input ? input.value.trim() : '';
    if (!issue || !issue.field) return;
    if (!value) {
      this.setStatus(`Type the ${issue.text.replace(/^No /, '')} first.`, true);
      return;
    }
    if (this.savedIssues.has(key) || this.saving.has(`np:${key}`)) return;
    // What is STILL missing once this one is saved: the ones saved earlier this visit are done too.
    const remaining = problem.data.issues.filter((_, i) => i !== index && !this.savedIssues.has(`${id}|${i}`));
    this.saving.add(`np:${key}`);
    this.render();
    try {
      await postOp(newProductFixUrl(this.adminUrl), this.adminSecret, 'fix', {
        tillId: problem.data.tillId,
        field: issue.field,
        value: value,
        remaining: remaining,
        who: this.dataset.customerName || 'Problems page',
      });
      this.saving.delete(`np:${key}`);
      // THE SAVED DETAIL STAYS, GREYED (Jonathan, 28/10/2026); once none is left the whole product
      // greys as cleared. The issue list is left whole so each line keeps its place.
      this.savedIssues.add(key);
      if (!remaining.length) this.clearedProblems.add(id);
      // NO POP-UP WHEN IT WORKED (Jonathan, 28/10/2026): the saved line greys.
      this.render();
    } catch (error) {
      this.saving.delete(`np:${key}`);
      this.render();
      this.setStatus(`Couldn't save that: ${error.message}`, true);
    }
  }

  /**
   * THE CLEAR BUTTON IN ITS THREE STATES (Jonathan, 28/10/2026): Clear; greyed with a spinner while
   * the clear saves; and Cleared once it has, with the row greyed and left where it was - so the
   * list does not shift under somebody working down it. A reload drops it, as only open problems load.
   */
  resolveButtonHtml(id) {
    const key = String(id);
    if (this.clearedProblems.has(key)) {
      return '<button type="button" class="stock-manager__resolve is-cleared" disabled>Cleared</button>';
    }
    if (this.saving.has(`sync:${key}`)) {
      return '<button type="button" class="stock-manager__resolve" disabled>Clear</button>';
    }
    if (this.saving.has(`resolve:${key}`)) {
      return '<button type="button" class="stock-manager__resolve" disabled>'
        + '<span class="stock-manager__spinner" aria-hidden="true"></span>Clearing</button>';
    }
    return `<button type="button" class="stock-manager__resolve" data-resolve="${escapeHtml(key)}">Clear</button>`;
  }

  clearedRowClass(id) {
    return this.clearedProblems.has(String(id)) ? ' is-cleared' : '';
  }

  problemRowHtml(problem) {
    const { flag, field, format } = problemFlag(problem.id);
    // The section that carries the priority flag is the one the product sits in,
    // i.e. the LEAF - the same section the counting page would send someone to.
    const found = sectionPathByName(this.sections, problem.section);
    const path = found.names;
    const leaf = found.leaf;

    // Both sides are sent for every field and the flag decides which pair to show:
    // a price mismatch shows the two prices where a stock one shows the counts.
    const pair = (problem.values || {})[field] || {};

    // NO "AGREES NOW" MARKER ANY MORE (Jonathan, 13/08/2026). A problem whose two
    // figures agree is no longer a problem, so `listProblems` clears it server-side
    // before the page ever sees it - housekeeping that only happened when someone
    // opened a browser was not housekeeping. Nothing here needs to mark that state,
    // because nothing in that state arrives.
    return `
      <tr class="stock-manager__row${this.clearedRowClass(problem.id)}">
        <td class="stock-manager__when">
          ${problem.raisedAt ? escapeHtml(formatStamp(problem.raisedAt)) : '<span class="stock-manager__dash">&mdash;</span>'}
        </td>
        <td class="stock-manager__name">
          ${escapeHtml(problemName(problem))}
          <span class="stock-manager__flag">${escapeHtml(flag)}</span>
          ${PROBLEM_REASON_FLAGS.has(flag) && problemReason(problem)
            ? `<span class="stock-manager__reason">${escapeHtml(problemReason(problem))}</span>`
            : ''}
        </td>
        <td class="stock-manager__path">
          ${path.length
            ? path.map((n) => escapeHtml(n)).join(' <span aria-hidden="true">&rarr;</span> ')
            : '<span class="stock-manager__dash">&mdash;</span>'}
        </td>
        <td class="stock-manager__tick">
          ${leaf
            ? `<label>
                 <input type="checkbox" data-priority-for="${escapeHtml(leaf.id)}"
                        ${leaf.priority ? 'checked' : ''} ${this.priorityAvailable ? '' : 'disabled'}>
                 <span class="visually-hidden">Priority for the whole ${escapeHtml(leaf.name)} section</span>
               </label>`
            : '<span class="stock-manager__dash">&mdash;</span>'}
        </td>
        <td class="stock-manager__num">${escapeHtml(problemValue(pair.till, format))}</td>
        <td class="stock-manager__sync-col">${this.syncCellHtml(problem, flag, field, format, pair)}</td>
        <td class="stock-manager__num">${escapeHtml(problemValue(pair.web, format))}</td>
        <td class="stock-manager__act">
          ${this.resolveButtonHtml(problem.id)}
        </td>
      </tr>`;
  }

  /**
   * The two arrows, each pointing at the column it is about to overwrite.
   *
   * THE CELL SITS BETWEEN `Till` AND `Website` ON PURPOSE. `&rarr;` physically points
   * at the Website column it will change, and `&larr;` at the Till column, so the
   * arrows explain themselves. A Sync column bolted onto the end of the row would
   * need a legend saying which way round it was, and a legend on a control that
   * writes to a live shop is a legend somebody will misread.
   *
   * THE BUTTONS CARRY NO FIGURE (14/08/2026, Jonathan). They used to show the value
   * they were about to write, which put a fourth and fifth number into a row that
   * already has Till and Website side by side - the arrow says which way, and the two
   * columns either side already say what. The value is still on the button for anyone
   * who cannot see it, in `aria-label`; dropping it from the face is a change to what
   * is DRAWN, not to what is announced.
   *
   * They are also STACKED rather than side by side, and drawn as SVG rather than set
   * as text. `&larr;` and `&rarr;` are hairline glyphs in most faces and ignore
   * `font-weight` entirely - a bold arrow is simply not a thing most fonts contain -
   * so asking for a thicker one in CSS does nothing. A stroked path is the only way
   * to control the weight, and it renders identically in both themes because it
   * inherits `currentColor`.
   *
   * ARMED, THEN CONFIRMED - the one place on this page where a single click is not
   * enough. The priority ticks and `Clear` commit immediately and should: the worst a
   * stray click does there is change what someone is asked to count. These two put a
   * price in front of customers or move real stock, so the first click only arms the
   * button and the second one writes. Any other click on the page disarms it.
   *
   * A row that is NOT a settleable disagreement gets a dash, the same as every other
   * column here does when it has nothing to say.
   */
  syncCellHtml(problem, flag, field, format, pair) {
    if (!syncablePair(flag, field, pair)) {
      return '<span class="stock-manager__dash">&mdash;</span>';
    }

    const tillText = problemValue(pair.till, format);
    const webText = problemValue(pair.web, format);
    const busy = this.saving.has(`sync:${problem.id}`) || this.clearedProblems.has(String(problem.id));

    const arrow = (direction, path, writes, label) => {
      const key = `${problem.id}|${direction}`;
      const armed = this.armedSync === key;
      // `Release` is a different word from `Confirm` on purpose: it is a different
      // question. Confirm asks "did you mean to click that"; Release asks "do you
      // really want this high-demand stock on sale to customers".
      const release = this.highDemandArmed === key;
      // THE ARMED STATE KEEPS ITS WORD even though the resting state lost its figure.
      // These two are not the same kind of thing: the figure was information the row
      // already carried twice, while `Confirm` is the only thing distinguishing a
      // button that arms from one that is about to move real stock. Colour alone must
      // not be what says so.
      // THE ARROW THAT WAS CLICKED SPINS (14/08/2026, Jonathan). A write re-reads both
      // catalogues before it does anything, so there are a few seconds where the only
      // signal was the button greying - which reads as nothing happening, and did.
      const spinning = this.syncingArrow === key;
      const face = spinning
        ? '<span class="stock-manager__spinner stock-manager__sync-spinner" aria-hidden="true"></span>'
        : release
          ? 'Release'
          : armed
            ? 'Confirm'
            : `<svg class="stock-manager__sync-glyph" viewBox="0 0 24 24" aria-hidden="true"
                    fill="none" stroke="currentColor" stroke-width="3"
                    stroke-linecap="round" stroke-linejoin="round"><path d="${path}"/></svg>`;
      return `<button type="button"
                class="stock-manager__sync-arrow${armed || release ? ' is-armed' : ''}${release ? ' is-release' : ''}"
                data-sync-fix="${escapeHtml(problem.id)}"
                data-direction="${direction}"
                ${busy ? 'disabled' : ''}
                aria-label="${escapeHtml(release ? `Release — ${label}` : label)}">
          ${face}
        </button>`;
    };

    // RIGHT-POINTING ON TOP (14/08/2026, Jonathan). Writing the till's figure to the
    // website is the common answer - the till is master for stock, and the 13/08
    // backlog was settled that way - so the arrow reached for most sits first.
    return `<span class="stock-manager__sync-stack">${arrow(
      'toWebsite',
      'M4 12h16M14 6l6 6-6 6',
      tillText,
      `Make the website match the till — set the website to ${tillText}`,
    )}${arrow(
      'toTill',
      'M20 12H4M10 6l-6 6 6 6',
      webText,
      `Make the till match the website — set the till to ${webText}`,
    )}</span>`;
  }

  /**
   * The three views as tabs, with only the chosen one rendered.
   *
   * `tabs` is `[key, label, body]`. The body of a hidden tab is not built at all -
   * these are the three most expensive things on the page, and there is no reason to
   * lay out a 138-row section table nobody is looking at.
   *
   * Proper tab semantics rather than a row of buttons: `role="tablist"` with
   * `aria-selected` and `aria-controls`, and left/right arrows move between them, which
   * is what a screen reader and a keyboard user expect from something that looks like
   * this. The buttons are real `<button>`s, so Enter and Space work for free.
   */
  tabsHtml(tabs) {
    const strip = tabs.map(([key, label]) => `
      <button type="button" role="tab" id="tab-${escapeHtml(key)}"
              class="stock-manager__tab${this.activeTab === key ? ' is-on' : ''}"
              data-tab="${escapeHtml(key)}"
              aria-selected="${this.activeTab === key ? 'true' : 'false'}"
              aria-controls="panel-${escapeHtml(key)}"
              tabindex="${this.activeTab === key ? '0' : '-1'}">
        ${escapeHtml(label)}
      </button>`).join('');

    const active = tabs.find(([key]) => key === this.activeTab) ?? tabs[0];
    return `
      <div class="stock-manager__tabs" role="tablist" aria-label="Stock overview sections">
        ${strip}
      </div>
      <div class="stock-manager__tabpanel" role="tabpanel" id="panel-${escapeHtml(active[0])}"
           aria-labelledby="tab-${escapeHtml(active[0])}">
        ${active[2]()}
      </div>`;
  }

  /**
   * Put some of a high-demand product's held stock on sale.
   *
   * WHY THIS BUTTON EXISTS AT ALL. A flagged product's stock is synced into a
   * held-back bucket on the website, so the shop's figure is visible while
   * nothing is buyable. Shopify will NOT let a merchant move that stock by hand -
   * it belongs to the app that held it, which Shopify has confirmed is a
   * limitation of their API - so this is the only door.
   *
   * TWO CLICKS, DELIBERATELY. Releasing is the one action here that cannot be
   * undone: Pokemon and the like are cleared by bots within minutes of becoming
   * buyable, and an accidental release of 180 boxes is not recoverable by
   * pressing the button again.
   *
   * THE SERVER OWNS THE FIGURES. The page does not track how much is held - it
   * asks, and reports what comes back. A held figure drawn minutes ago and acted
   * on now is exactly the kind of stale number this project keeps being bitten
   * by, so the answer always comes from the write itself.
   */
  async onRelease(button) {
    const id = String(button.dataset.releaseProduct);
    const name = button.dataset.releaseName || id;
    const field = this.querySelector(`[data-release-qty="${id}"]`);
    const quantity = Number((field && field.value) || 0);

    if (!Number.isInteger(quantity) || quantity <= 0) {
      this.releaseArmed = null;
      this.setStatus('Type how many to put on sale first.');
      this.render();
      return;
    }

    if (this.releaseArmed !== id) {
      this.releaseArmed = id;
      this.setStatus(`Put ${quantity} of ${name} on sale? Press again to confirm.`);
      this.render();
      return;
    }

    this.releaseArmed = null;
    button.disabled = true;
    button.textContent = 'Releasing…';
    try {
      const out = await postOp(this.adminUrl, this.adminSecret, 'releaseStock', {
        customerId: this.customerId,
        itemId: id,
        itemName: name,
        quantity,
      });
      if (out && out.ok) {
        // The server's own reading, taken after the move and checked - not what
        // the page hoped would happen.
        const now = out.now || {};
        this.setStatus(`${name}: ${out.released} now on sale, ${now.held} still held back.`);
        this.releaseQty.delete(id);
        if (field) field.value = '';
        await this.refreshHd();
      } else {
        this.setStatus((out && out.error) || 'The release did not go through.');
      }
    } catch (err) {
      this.setStatus(`The release could not be sent: ${err.message}`);
    }
    this.render();
  }

  highDemandHtml() {
    if (!this.highDemandAvailable) {
      return '<p class="stock-manager__muted">High demand isn&rsquo;t switched on yet.</p>';
    }

    const results = this.searchResults;
    const flagged = new Set(this.hdProducts.map((p) => String(p.id)));

    let resultsHtml = '';
    if (results === 'searching') {
      resultsHtml = spinnerHtml('Loading…');
    } else if (Array.isArray(results)) {
      resultsHtml = results.length
        ? `<ul class="stock-manager__found">${results
            .map((r) => {
              // BEING A SUB SAYS WHERE YOUR STOCK LIVES, NOT WHETHER YOU CAN BE FLAGGED
              // (Jonathan, 16/08/2026). This used to hide the control on the grounds
              // that "a tick here would do nothing". That was wrong, and checked rather
              // than assumed: `highDemandFor` keys on the product's own id and
              // `stockChange` is called per product, so flagging a sub genuinely holds
              // it back - and it is the ONLY way to hold back loose boosters.
              // The note stays, because where the stock is counted is still worth
              // knowing; the checkbox comes back.
              const byMaster = !!r.controlledBy;
              // No stock figure here: picking a product to hold back is not a
              // counting job, and the number only competes with the name. The
              // levels that matter are on the flagged list below, next to Sync.
              const inner = `
                  <span class="stock-manager__found-name">${escapeHtml(r.name)}${
                    byMaster
                      ? ' <span class="stock-manager__found-note">(Stock counted on the master product)</span>'
                      : ''
                  }</span>
                  <span class="stock-manager__found-section">${escapeHtml(r.categoryName || '—')}</span>`;
              return `<li${byMaster ? ' class="stock-manager__found--by-master"' : ''}>${
                `<label>
                  <input type="checkbox" data-demand-prod="${escapeHtml(r.productId)}"
                         data-demand-name="${escapeHtml(r.name)}"
                         ${flagged.has(String(r.productId)) ? 'checked' : ''}>${inner}
                </label>`
              }</li>`;
            })
            .join('')}</ul>`
        : `<p class="stock-manager__muted">Nothing matched${
            this.showOutOfStock ? '' : ' that is in stock. Tick &ldquo;Show out of stock products&rdquo; to look wider'
          }.</p>`;

      // Never cut the list silently. In stock only, "collector" is 19 matches and
      // fits - but "booster" is 189 and "box" 215, so a broad search still hits
      // the limit, and a manager has to be able to see that it did.
      if (this.searchTruncated) {
        resultsHtml += `<p class="stock-manager__muted">Showing the first ${
          this.searchResults.length
        } of ${this.searchTotal} matches &mdash; type a bit more to narrow it down.</p>`;
      }
    }

    const list = this.hdSectionHtml(flagged);

    return `
      <p class="stock-manager__muted">Stock for anything high demand is never put on sale automatically &mdash; the website shows what the shop holds, but none of it is buyable until somebody releases it here, so bots can&rsquo;t clear it. Open a section below, type how many to put on sale and press Release twice. Whole sections are ticked on the Sections tab; search here to flag a single product.</p>
      <div class="stock-manager__search">
        <label class="visually-hidden" for="hd-search">Search products</label>
        <input type="search" id="hd-search" data-demand-search placeholder="Search for a product by name or barcode&hellip;" autocomplete="off">
        <label class="stock-manager__search-toggle">
          <input type="checkbox" data-demand-oos ${this.showOutOfStock ? 'checked' : ''}>
          Show out of stock products
        </label>
      </div>
      ${resultsHtml}
      <h3 class="stock-manager__subhead">High demand stock by section</h3>
      ${list}`;
  }

  /**
   * THE HIGH-DEMAND PRODUCTS, SECTION BY SECTION (Jonathan, 13/10/2026): "display sections, like
   * the Stock Check tab does ... Drilling down into the section should then list all of the
   * products which are high demand in that section and allow you to release the stock on the
   * right-hand side. If a section doesn't have any high-demand products in it, then it shouldn't
   * be displayed". The server decides what is high demand and which sections hold any - the same
   * rule the sync uses - so this only draws what it is sent.
   */
  hdSectionHtml(flagged) {
    const view = this.hdTree;
    if (!view) {
      if (!this.hdLoading) Promise.resolve().then(() => this.loadHdTree());
      return spinnerHtml('Loading…');
    }
    if (view.error) return `<p class="stock-manager__muted">${escapeHtml(view.error)}</p>`;
    const node = this.hdNode;
    const byId = new Map((view.tree || []).map((c) => [c.id, c]));
    // THE SECTION IS DRAWN FROM WHAT THE PAGE ALREADY HOLDS (Jonathan, 13/10/2026: "On the stock
    // check page, the sections load instantly"). Only the website's on-sale and held figures are
    // asked for per section, and they fill in when they arrive.
    const path = [];
    for (let c = byId.get(node), guard = 0; c && guard < 20; c = byId.get(c.parentId), guard += 1) path.unshift(c);
    let html = '';
    if (path.length) {
      const crumbs = ['<button type="button" data-hd-node="">All sections</button>'].concat(
        path.map((c, i) => (i === path.length - 1
          ? `<span aria-current="page">${escapeHtml(c.name)}</span>`
          : `<button type="button" data-hd-node="${escapeHtml(c.id)}">${escapeHtml(c.name)}</button>`)),
      );
      html += `<nav class="stock-manager__crumbs" aria-label="Section">${crumbs.join(
        '<span class="stock-manager__crumb-sep" aria-hidden="true">›</span>')}</nav>`;
    }
    const sections = (view.tree || []).filter((c) => c.parentId === node);
    if (sections.length) {
      html += `<ul class="stock-manager__hd-sections">${sections.map((c) => `
        <li><button type="button" class="stock-manager__hd-section" data-hd-node="${escapeHtml(c.id)}">
          <span class="stock-manager__hd-section-name">${escapeHtml(c.name)}</span>
          <span class="stock-manager__hd-section-count">${escapeHtml(c.count)} with stock</span>
        </button></li>`).join('')}</ul>`;
    }
    const products = node ? (view.all || []).filter((p) => p.categoryId === node) : [];
    const live = this.hdLive.get(node);
    if (products.length && !live && !this.hdLiveLoading.has(node)) {
      Promise.resolve().then(() => this.loadHdLive(node));
    }
    if (products.length) {
      if (sections.length) html += '<h3 class="stock-manager__subhead">Also in this section</h3>';
      html += `<ul class="stock-manager__found">${products.map((p) => {
        // A product flagged ON ITS OWN keeps its tick box here, so it can be unflagged where it is
        // seen (Jonathan, 13/10/2026: "I like the idea of having the tick box next to individual
        // products that have been flagged"). One high demand through its section has none - that
        // is changed on the Sections tab. Otherwise every product is drawn the same, and found by
        // drilling down or by the search above.
        const note = p.listed ? '' : ' <span class="stock-manager__found-note">(no website listing)</span>';
        const name = flagged.has(String(p.id))
          ? `<label>
               <input type="checkbox" data-demand-prod="${escapeHtml(p.id)}"
                      data-demand-name="${escapeHtml(p.name)}" checked>
               <span class="stock-manager__found-name">${escapeHtml(p.name)}${note}</span>
             </label>`
          : `<div class="stock-manager__found-row"><span class="stock-manager__found-name">${escapeHtml(p.name)}${note}</span></div>`;
        // NOTHING HELD BACK, NOTHING TO RELEASE (Jonathan, 28/10/2026): the box and button grey out
        // when the website holds none of it back - and until its figures have arrived, so the
        // button is never live on a guess.
        const liveFigures = live ? live.get(String(p.id)) : null;
        const canRelease = !!liveFigures && Number(liveFigures.held) > 0;
        const off = canRelease ? '' : ' disabled';
        const release = p.listed ? `
            <div class="stock-manager__release">
              <label class="visually-hidden" for="rel-${escapeHtml(p.id)}">How many to put on sale</label>
              <input type="number" min="1" step="1" inputmode="numeric"
                     id="rel-${escapeHtml(p.id)}" class="stock-manager__release-qty"
                     data-release-qty="${escapeHtml(p.id)}" placeholder="0"
                     value="${escapeHtml(this.releaseQty.get(String(p.id)) || '')}"${off}>
              <button type="button" class="stock-manager__sync"
                      data-release-product="${escapeHtml(p.id)}"
                      data-release-name="${escapeHtml(p.name)}"${off}
                      ${canRelease ? '' : `title="${liveFigures ? 'Nothing is held back to release' : 'Reading the website figures'}"`}>
                ${this.releaseArmed === String(p.id) ? 'Confirm?' : 'Release'}
              </button>
            </div>` : '';
        const picture = window.StaffPictures
          ? window.StaffPictures.html(p, { className: 'stock-manager__thumb', lazy: true, title: p.name })
          : '';
        // Until the website's figures arrive, on sale reads as an ellipsis and nothing is compared.
        const figures = live ? (live.get(String(p.id)) || {}) : null;
        const levels = figures
          ? { eposStock: p.till, onSale: figures.onSale, heldStock: figures.held, presold: figures.presold }
          : { eposStock: p.till, onSale: '…', heldStock: null };
        return `<li class="stock-manager__hd-product">${picture}${name}
            <div class="stock-manager__flow">${release}
              ${levelsHtml(levels)}
            </div></li>`;
      }).join('')}</ul>`;
    } else if (!sections.length) {
      html += '<p class="stock-manager__muted">Nothing high demand holds stock here.</p>';
    }
    return html;
  }

  /** Every high-demand section and product, once. `quiet` keeps the current view up. */
  async loadHdTree(quiet) {
    this.hdLoading = true;
    if (!quiet) this.render();
    try {
      const data = await postOp(this.adminUrl, this.adminSecret, 'listHighDemandSections', {
        customerId: this.customerId,
        categoryId: '',
      });
      this.hdTree = data && (Array.isArray(data.tree) || data.error) ? data
        : { error: 'The high-demand sections could not be read.' };
      // A section that has gone (its last product sold or unflagged) drops back to the top.
      if (this.hdNode && !(this.hdTree.tree || []).some((c) => c.id === this.hdNode)) this.hdNode = '';
    } catch (error) {
      this.hdTree = { error: `The high-demand sections could not be read: ${error.message}` };
    }
    this.hdLoading = false;
    this.render();
  }

  /** The website's on-sale and held figures for one section's products. */
  async loadHdLive(node) {
    this.hdLiveLoading.add(node);
    try {
      const data = await postOp(this.adminUrl, this.adminSecret, 'listHighDemandSections', {
        customerId: this.customerId,
        categoryId: node,
      });
      const figures = new Map();
      for (const p of (data && data.products) || []) {
        figures.set(String(p.id), { onSale: p.onSale, held: p.held, presold: p.presold });
      }
      this.hdLive.set(node, figures);
    } catch (error) {
      // Dashes rather than a stuck ellipsis: an empty map reads as "not known".
      this.hdLive.set(node, new Map());
      this.setStatus(`The website figures could not be read: ${error.message}`, true);
    }
    this.hdLiveLoading.delete(node);
    if (this.hdNode === node) this.render();
  }

  /** After a release or a flag change: the tree and this section's figures are asked for again. */
  async refreshHd() {
    this.hdLive.delete(this.hdNode);
    await this.loadHdTree(true);
  }


  render() {
    const rows = this.ordered();
    const table = rows.length
      ? `<div class="stock-manager__scroll">
           <table class="stock-manager__table">
             <thead>
               <tr>
                 ${this.sortHeading('name', 'Section')}
                 ${this.sortHeading('checked', 'Last checked')}
                 ${this.sortHeading('products', 'Products', 'stock-manager__num')}
                 ${this.sortHeading('priority', 'Priority', 'stock-manager__tick')}
                 ${this.sortHeading('demand', 'High demand', 'stock-manager__tick')}
               </tr>
             </thead>
             <tbody>${rows.map((r) => this.rowHtml(r)).join('')}</tbody>
           </table>
         </div>`
      : '<p class="stock-manager__muted">No sections found.</p>';

    const priorityNote = this.priorityAvailable
      ? ''
      : '<p class="stock-manager__muted">Priority ticks aren&rsquo;t switched on yet.</p>';

    // Bodies are thunks so only the visible tab's is built.
    this.innerHTML = `
      ${this.tabsHtml([
        ['problems', 'Problems', () => this.problemsBody()],
        ['sections', 'Sections', () => priorityNote + table],
        ['demand', 'High demand', () => this.highDemandHtml()],
      ])}
      <p class="stock-manager__status" data-status role="status" hidden></p>`;

    // The status node has just been rebuilt empty along with everything else, so repaint
    // it from state. Without this a second click wipes the first click's confirmation -
    // which is exactly what happened when two rows were settled in quick succession.
    this.paintStatus();

    // Restore focus and the caret after a re-render, so typing in the search box is not
    // interrupted by the results arriving.
    if (this.refocusSearch) {
      const box = this.querySelector('[data-demand-search]');
      if (box) {
        box.value = this.searchTerm || '';
        box.focus();
        box.setSelectionRange(box.value.length, box.value.length);
      }
      this.refocusSearch = false;
    }

  }

  onInput(event) {
    const qty = event.target.closest('[data-release-qty]');
    if (qty) {
      const id = String(qty.dataset.releaseQty);
      this.releaseQty.set(id, qty.value);
      // A changed number is a new question: an armed button must not confirm a figure it never showed.
      if (this.releaseArmed === id) {
        this.releaseArmed = null;
        const button = this.querySelector(`[data-release-product="${id}"]`);
        if (button) button.textContent = 'Release';
      }
      return;
    }
    const box = event.target.closest('[data-demand-search]');
    if (!box) return;
    this.searchTerm = box.value;
    clearTimeout(this.searchTimer);
    const term = box.value.trim();
    if (term.length < 2) {
      const wasShowing = this.searchResults !== null;
      this.searchResults = null;
      // Emptying the box has to empty the list too. This used to skip the
      // re-render to avoid stealing focus, which left the last search sitting
      // under a blank box - reading as though those were the current results.
      // `refocusSearch` puts the caret back at the end, so the render is safe;
      // only doing it when something was actually showing keeps every keystroke
      // below the minimum from redrawing for nothing.
      if (wasShowing) {
        this.refocusSearch = true;
        this.render();
      }
      return;
    }
    this.searchTimer = setTimeout(() => this.runSearch(term), 300);
  }

  async runSearch(term) {
    this.searchResults = 'searching';
    this.refocusSearch = true;
    this.render();
    try {
      // The counting page's own search, reused rather than reimplemented - it already
      // splits multi-barcode fields, which a second implementation would get wrong.
      const data = await postOp(this.url, this.secret, 'search', {
        customerId: this.customerId,
        query: term,
        // Flagging high demand writes to the HighDemand tab, keyed on the product
        // id - it never touches stock, so a product with no stock row of its own
        // is perfectly flaggable. The counting page needs the opposite and gets
        // it by not asking. Every loose booster lives here: its stock sits on its
        // master's record as a volume, which is why `PKM Pitch Black Booster`
        // could not be found at all (reported 09/08/2026).
        includeUnstocked: true,
        // Out of stock is hidden unless asked for. A product whose stock sits on
        // a master's record still counts as in stock - loose boosters have no
        // stock row of their own, and judging them by that absent row is what
        // made them unfindable on 09/08.
        includeOutOfStock: this.showOutOfStock,
      });
      // Sorted by section, then name. A high-demand search pulls products from all over
      // the shop, so grouping them by shelf is what makes a flat list readable - and it
      // puts everything from one section together, which is usually how a manager is
      // thinking when they go looking.
      // NO SLICE HERE. This used to keep 25 of the server's 50, so a search could
      // drop 81 of its 106 matches and look complete - which is exactly how the
      // Hobbit Collector Box went missing. The server caps the list and now says
      // how many it matched, so the page reports the cut instead of making one.
      this.searchTotal = Number(data.total) || (data.items || []).length;
      this.searchTruncated = !!data.truncated;
      this.searchResults = (data.items || []).slice().sort((a, b) => {
        const bySection = String(a.categoryName || '').localeCompare(String(b.categoryName || ''));
        return bySection || String(a.name || '').localeCompare(String(b.name || ''));
      });
    } catch (error) {
      this.searchResults = [];
      this.setStatus(`Search failed: ${error.message}`, true);
    }
    this.refocusSearch = true;
    this.render();
  }

  async setHighDemand(kind, id, name, wanted) {
    await postOp(this.adminUrl, this.adminSecret, 'setHighDemand', {
      customerId: this.customerId,
      kind: kind,
      itemId: String(id),
      itemName: String(name || ''),
      highDemand: wanted,
    });
    if (kind === 'category') {
      if (wanted) this.hdCategories.add(String(id));
      else this.hdCategories.delete(String(id));
    } else {
      this.hdProducts = this.hdProducts.filter((p) => String(p.id) !== String(id));
      if (wanted) {
        // The two stock levels come from the server; a locally-added row has
        // none, which is why a product read as two dashes the moment it was
        // ticked. Show it straight away so the tick feels immediate, then take
        // the figures from the refresh below.
        this.hdProducts.push({ id: String(id), name: String(name || ''),
                               eposStock: null, shopifyStock: null });
      }
      this.hdProducts.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      if (wanted) await this.refreshHighDemand();
    }
  }

  /** Re-read the flagged list, which is the only source of the stock levels. */
  async refreshHighDemand() {
    try {
      const data = await postOp(this.adminUrl, this.adminSecret, 'listHighDemand', {
        customerId: this.customerId,
      });
      if (data && Array.isArray(data.products)) this.hdProducts = data.products.slice();
    } catch (error) {
      // The tick itself already succeeded, so a failed refresh must not read as
      // a failed change - the levels just stay as dashes until the next load.
      this.setStatus(`Flagged, but the stock levels could not be read: ${error.message}`, true);
    }
  }

  async onChange(event) {
    const oos = event.target.closest('[data-demand-oos]');
    if (oos) {
      this.showOutOfStock = oos.checked;
      const term = (this.searchTerm || '').trim();
      // Re-ask rather than filter what is already here: the server's list was cut
      // to a limit BEFORE this tick was applied, so the products this reveals are
      // ones the page was never sent.
      if (term.length >= 2) this.runSearch(term);
      else this.render();
      return;
    }

    const demandCat = event.target.closest('[data-demand-cat]');
    const demandProd = event.target.closest('[data-demand-prod]');
    if (demandCat || demandProd) {
      const el = demandCat || demandProd;
      const kind = demandCat ? 'category' : 'product';
      const id = demandCat ? el.dataset.demandCat : el.dataset.demandProd;
      const name = demandCat
        ? (this.sections.find((c) => String(c.id) === String(id)) || {}).name
        : el.dataset.demandName;
      const wanted = el.checked;
      el.disabled = true;
      const unbusy = tickBusy(el);
      try {
        await this.setHighDemand(kind, id, name, wanted);
        this.setStatus(`${name || id} ${wanted ? 'marked high demand' : 'no longer high demand'}.`);
        // The sections on the High demand tab follow the flags, so ask again once one changes.
        if (this.hdTree) await this.refreshHd();
        this.render();
      } catch (error) {
        // Put the tick back. A checkbox left changed after a failed save is a lie about
        // whether stock will reach the website.
        unbusy();
        el.checked = !wanted;
        el.disabled = false;
        this.setStatus(`Couldn't save that: ${error.message}`, true);
      }
      return;
    }

    const box = event.target.closest('[data-priority-for]');
    if (!box) return;
    const id = String(box.dataset.priorityFor);
    const wanted = box.checked;
    const node = this.sections.find((c) => String(c.id) === id);

    this.saving.add(id);
    box.disabled = true;
    const unbusy = tickBusy(box);
    try {
      await postOp(this.adminUrl, this.adminSecret, 'setPriority', {
        customerId: this.customerId,
        categoryId: id,
        priority: wanted,
      });
      if (node) node.priority = wanted;
      this.setStatus(
        `${node ? node.name : 'Section'} ${wanted ? 'flagged as a priority' : 'no longer a priority'}.`,
      );
      // Re-render so the row's highlight and the summary tile both follow, rather
      // than the tick being the only thing that moved.
      this.saving.delete(id);
      this.render();
      return;
    } catch (error) {
      // Put the tick back where it was. A checkbox that stays changed after a
      // failed save is a lie about what staff will see on the counting page.
      unbusy();
      box.checked = !wanted;
      this.setStatus(`Couldn't save that: ${error.message}`, true);
    }
    this.saving.delete(id);
    box.disabled = false;
  }

  /**
   * One arrow click: arm it, or - if it was already armed - actually write.
   *
   * The write itself is the server's job and deliberately so. It re-reads both sides
   * before touching anything, repoints the sync's memory for exactly the field it
   * wrote, re-reads again to confirm the two now agree, and only then clears the
   * problem. None of that can live here: a page cannot be trusted to have told the
   * truth about what it was showing, and a browser closed mid-write must not be able
   * to leave a catalogue written and the sync's memory still describing the world as
   * it was before. That combination is what put +37 phantom units onto six Games
   * Workshop paints on 13/08/2026.
   *
   * So there are three answers to handle, not two:
   *
   * - **`conflict`** - one of the two figures moved between the page loading and the
   *   click, usually a sale landing mid-read. Nothing is written. The row is updated
   *   to the figures the server has just seen and the manager decides again, which is
   *   the same bargain `setStock` makes with staff on the counting page.
   * - **success** - the problem is settled and gone, so the row goes with it.
   * - **anything else** - say so and leave the row exactly where it is.
   */
  async onSyncArrow(button) {
    const id = String(button.dataset.syncFix);
    const direction = String(button.dataset.direction);
    const key = `${id}|${direction}`;

    const problem = this.problems.find((p) => String(p.id) === id);
    if (!problem) return;
    const { flag, field, format } = problemFlag(problem.id);
    const pair = (problem.values || {})[field] || {};
    if (!syncablePair(flag, field, pair)) return;

    const writes = direction === 'toTill' ? pair.web : pair.till;
    const writesText = problemValue(writes, format);
    const side = direction === 'toTill' ? 'till' : 'website';
    const name = problemName(problem);

    // A release that has already been asked about goes straight through - the manager
    // has now answered two questions about this one arrow and a third would be noise.
    const releasing = this.highDemandArmed === key;

    // FIRST CLICK ARMS. Arming a different arrow drops the one before it, so the page
    // never holds two live writes waiting on a second click.
    // ARMING SAYS NOTHING (14/08/2026, Jonathan). The button itself already reads
    // `Confirm`, which is the whole message; a toast restating the row a manager is
    // looking at is noise, and noise on a channel reserved for refusals makes the
    // refusals easier to ignore. Anything the page has to SAY still gets said.
    if (!releasing && this.armedSync !== key) {
      this.armedSync = key;
      this.highDemandArmed = null;
      this.render();
      this.focusArrow(id, direction);
      this.setStatus('');
      return;
    }

    this.armedSync = null;
    this.highDemandArmed = null;
    // WHICH arrow is in flight, not merely that the row is busy. Both arrows on a row
    // are disabled while a write is running, but only the one that was clicked should
    // turn into a spinner - a row with two spinners would suggest two writes.
    this.syncingArrow = key;
    this.saving.add(`sync:${id}`);
    this.render();

    let data;
    try {
      data = await postSyncOp(this.adminUrl, this.adminSecret, {
        customerId: this.customerId,
        problemId: id,
        field: field,
        direction: direction,
        // The RAW values, not what the cell printed - the server compares these against
        // what it reads live, and `£2.50` is not a number.
        expectedTill: pair.till,
        expectedWeb: pair.web,
        dryRun: false,
        // Only ever true on the second click, and only after the server has asked.
        confirmHighDemand: releasing,
      });
    } catch (error) {
      this.syncingArrow = null;
      this.saving.delete(`sync:${id}`);
      this.render();
      this.setStatus(`Couldn't sync that: ${error.message}`, true);
      return;
    }
    // Cleared HERE, before any of the outcome branches below, so no path out of this
    // method can leave an arrow spinning for ever - several of them return early.
    this.syncingArrow = null;
    this.saving.delete(`sync:${id}`);

    // ORDER MATTERS BELOW. Four of these answers carry `ok:false` and three carry an
    // `error`, so a general failure test put any earlier would swallow the ones that
    // are really questions.

    // THE SHOP WAS CHANGED AND THE SYNC WAS NOT TOLD. The loudest thing this page can
    // say, and the only outcome that leaves the shop in a state nobody asked for: the
    // catalogue moved and the sync's memory still describes the world as it was, which
    // is the exact shape that put +37 phantom units onto six Games Workshop paints.
    // The next run may mirror this straight back out.
    if (data.repointed === false) {
      this.render();
      this.setStatus(
        `${name}: the ${data.wrote === 'till' ? 'till' : 'website'} was changed to ` +
          `${problemValue(data.target, format)}, but the sync could not be told about it ` +
          `(${data.error || 'no reason given'}). Leave this row alone and tell Jonathan — ` +
          `the next sync run may undo it.`,
        true,
      );
      return;
    }

    // A HIGH-DEMAND RELEASE, which is a second and different question. Nothing has been
    // written; the server is asking whether this stock should go on sale to customers.
    if (data.needsConfirm === 'highDemand') {
      this.highDemandArmed = key;
      this.updatePair(problem, field, data.till, data.web);
      this.render();
      this.focusArrow(id, direction);
      this.setStatus(
        `${name} is high demand — this puts ${data.adding} more on sale to customers. ` +
          `Click Release if that is what you want.`,
      );
      return;
    }

    // One of the two figures moved between the page loading and the click. Ordinary,
    // not a fault: these come from the sync's memory and can be a run old.
    if (data.conflict) {
      this.updatePair(problem, field, data.till, data.web);
      this.render();
      // DO NOT CLAIM IT MOVED WHILE THE PAGE WAS OPEN (14/08/2026). This branch knows
      // only that the live figures differ from the ones the row was drawn with, which
      // is just as often a row that was already out of date when it loaded. Saying
      // "moved while the page was open" asserts a timing nothing here observed, and it
      // read as the page failing to notice a write that had in fact happened earlier.
      this.setStatus(
        `Nothing was changed — ${name} no longer reads what this row shows. It is now ` +
          `${problemValue(data.till, format)} at the till and ` +
          `${problemValue(data.web, format)} on the website. Have another look.`,
        true,
      );
      return;
    }

    if (data.ok === false) {
      this.render();
      this.setStatus(`Couldn't sync that: ${data.error || 'it was refused.'}`, true);
      return;
    }

    // Written, and the two STILL disagree. The problem deliberately stays open - it has
    // not been settled just because something was written at it.
    if (data.resolved === false) {
      this.updatePair(problem, field, data.till, data.web);
      this.render();
      this.setStatus(
        `${name}: the ${side} was set to ${writesText}, but the two still disagree ` +
          `(${data.note || 'no reason given'}). Left on the list.`,
        true,
      );
      return;
    }

    // SETTLED, SO IT STAYS GREYED LIKE A CLEARED ONE (Jonathan, 28/10/2026), showing the figures
    // the two sides now hold, until the page reloads.
    if (data.till !== undefined && data.web !== undefined) this.updatePair(problem, field, data.till, data.web);
    this.clearedProblems.add(id);
    this.render();
    // NO POP-UP WHEN IT WORKED (Jonathan, 28/10/2026): the greyed row shows the figures it now holds.
    // Only "nothing was written" is said, because the greyed row cannot tell it apart.
    if (data.wrote === false) this.setStatus(`${name}: nothing to do — the two already agree.`);
  }

  /** The two figures the server has just read, replacing what the page believed. */
  updatePair(problem, field, till, web) {
    problem.values = Object.assign({}, problem.values, { [field]: { till: till, web: web } });
  }

  /** Put the keyboard back on an arrow the re-render has just replaced. */
  focusArrow(id, direction) {
    const again = this.querySelector(
      `[data-sync-fix="${CSS.escape(id)}"][data-direction="${direction}"]`,
    );
    if (again) again.focus();
  }

  async onClick(event) {
    // Found BEFORE anything re-renders. Disarming below replaces the table, which
    // detaches the node this event came from, and a `closest` run after that would
    // be walking a tree no longer on the page.
    const syncFix = event.target.closest('[data-sync-fix]');

    // An armed arrow is a safety catch, so it must not outlive the manager's
    // attention: ANY other click on the page puts it back. This runs before the
    // branches below so the click still does whatever else it was for.
    if ((this.armedSync || this.highDemandArmed) && !syncFix) {
      this.armedSync = null;
      this.highDemandArmed = null;
      this.releaseArmed = null;
      this.render();
    }

    if (syncFix) {
      await this.onSyncArrow(syncFix);
      return;
    }

    const hdNode = event.target.closest('[data-hd-node]');
    if (hdNode) {
      this.releaseArmed = null;
      this.hdNode = hdNode.dataset.hdNode;
      this.render();
      return;
    }

    const release = event.target.closest('[data-release-product]');
    if (release) {
      await this.onRelease(release);
      return;
    }

    const sort = event.target.closest('[data-sort]');
    if (sort) {
      const key = sort.dataset.sort;
      // Click a new heading for its natural order; click it again to flip. Names A-Z,
      // the oldest check first, and the biggest count or the ticked ones first.
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc';
      } else {
        this.sortKey = key;
        this.sortDir = key === 'name' || key === 'checked' ? 'asc' : 'desc';
      }
      this.render();
      const moved = this.querySelector(`[data-sort="${key}"]`);
      if (moved) moved.focus();
      return;
    }

    const openSection = event.target.closest('[data-open-section]');
    if (openSection) {
      // Stock tabs moves to the Stock check tab; Stock check then opens the section,
      // waiting for its section list if that has not loaded yet.
      document.dispatchEvent(new CustomEvent('dc:stock-show-tab', { detail: { key: 'check' } }));
      document.dispatchEvent(new CustomEvent('dc:stock-open-section', {
        detail: { id: openSection.dataset.openSection },
      }));
      window.scrollTo({ top: 0 });
      return;
    }

    const tab = event.target.closest('[data-tab]');
    if (tab) {
      if (this.activeTab !== tab.dataset.tab) {
        this.activeTab = tab.dataset.tab;
        this.render();
        // Keep the keyboard where the user left it - after a re-render the old button
        // is gone, so focus has to be put back on the new one by name.
        const moved = this.querySelector(`[data-tab="${this.activeTab}"]`);
        if (moved) moved.focus();
      }
      return;
    }

    const chip = event.target.closest('[data-flag-filter]');
    if (chip) {
      const wanted = chip.dataset.flagFilter || null;
      if (!wanted) {
        // `All` is the way out, and it is the only chip that clears rather than
        // toggles - with several selected, un-picking them one at a time is work.
        this.problemFilters.clear();
      } else if (this.problemFilters.has(wanted)) {
        this.problemFilters.delete(wanted);
      } else {
        this.problemFilters.add(wanted);
      }
      this.render();
      return;
    }

    const npSave = event.target.closest('[data-np-save]');
    if (npSave) {
      await this.saveNewProductDetail(String(npSave.dataset.npSave));
      return;
    }

    const button = event.target.closest('[data-resolve]');
    if (!button) return;
    const id = String(button.dataset.resolve);
    if (this.saving.has(`resolve:${id}`)) return;
    this.saving.add(`resolve:${id}`);
    this.render();
    try {
      await postOp(this.adminUrl, this.adminSecret, 'resolveProblem', {
        customerId: this.customerId,
        problemId: id,
      });
      this.saving.delete(`resolve:${id}`);
      // KEPT, GREYED, NOT REMOVED (Jonathan, 28/10/2026): the row stays where it is, under whatever
      // filter is showing, until the page reloads.
      this.clearedProblems.add(id);
      // NO POP-UP WHEN IT WORKED (Jonathan, 28/10/2026): the row greys and is struck through.
      this.render();
    } catch (error) {
      this.saving.delete(`resolve:${id}`);
      this.render();
      this.setStatus(`Couldn't save that: ${error.message}`, true);
    }
  }
}

if (!customElements.get('stock-check-manager')) {
  customElements.define('stock-check-manager', StockCheckManager);
}
})();
